
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { resolveApiBase } from '@/utils/apiBase';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  MapPin, 
  Search, 
  CheckCircle2, 
  Instagram,
  Facebook,
  Linkedin,
  Globe,
  LayoutDashboard,
  Users,
  FileText,
  MessageSquare,
  AlertCircle,
  TrendingUp,
  Award,
  DollarSign,
  Loader2,
  Briefcase,
  Ban,
  FileSearch,
  PenTool,
  Check,
  ExternalLink,
  Filter,
  Tag,
  X,
  Trash2,
  RefreshCw
} from 'lucide-react';
import Swal from 'sweetalert2';
import { Role, ImobiliariaStatus, ImobiliariaStatusBucket } from '@/types';
import { resolveImobiliariaStatus } from '@/utils/status';

const CORE_API = resolveApiBase(import.meta.env.VITE_CORE_API, '/core', 'http://localhost:3001');

const getAuthHeaders = (): Record<string, string> => {
  if (typeof window === 'undefined') return {};
  const rawToken = localStorage.getItem('va_token') || '';
  return rawToken ? { Authorization: rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}` } : {};
};

interface RealEstatePageProps {
  darkMode: boolean;
  role: Role;
  initialAgencyId?: number | null;
  initialTabSlug?: string | null;
}

type AgencyStatus = ImobiliariaStatus;
type AgencyStatusBucket = ImobiliariaStatusBucket;
const TAB_IDS = ['dashboard', 'registration', 'team', 'tickets', 'settings'] as const;
type RealEstateTab = typeof TAB_IDS[number];
const TAB_SLUGS: Record<RealEstateTab, string> = {
  dashboard: 'visao-geral',
  registration: 'dados-cadastrais',
  team: 'equipe',
  tickets: 'chamados',
  settings: 'configuracoes',
};
const SLUG_TO_TAB: Record<string, RealEstateTab> = {
  'visao-geral': 'dashboard',
  'dados-cadastrais': 'registration',
  equipe: 'team',
  chamados: 'tickets',
  configuracoes: 'settings',
  dashboard: 'dashboard',
  registration: 'registration',
  team: 'team',
  tickets: 'tickets',
  settings: 'settings',
};
const resolveTabFromSlug = (value?: string | null): RealEstateTab => {
  if (!value) return 'dashboard';
  const normalized = value.toLowerCase();
  return SLUG_TO_TAB[normalized] || 'dashboard';
};
const buildAgencyTabPath = (id: number, tab: RealEstateTab) => `/imobiliarias/${id}/${TAB_SLUGS[tab]}`;

// Interfaces for List View
interface RealEstateList {
  id: number;
  name: string;
  corporateName: string;
  status: AgencyStatus;
  phone: string;
  email: string;
  tags?: string[];
}

// Interfaces for Profile Detail View
interface DashboardMetrics {
  total_sales: string;
  active_leads: number;
  conversion_rate: string;
  ranking_position: number;
}

interface RegistrationData {
  fantasy_name: string;
  corporate_name: string;
  cnpj: string;
  creci_pj: string;
  address: string;
  neighborhood: string;
  zip: string;
  email: string;
  website: string;
  owner: {
    name: string;
    email: string;
    whatsapp: string;
  };
  socials: {
    instagram: string;
    facebook: string;
    linkedin: string;
  };
}

interface TeamMember {
  id: number;
  name: string;
  role: 'Gestor' | 'Corretor' | 'Sócio';
  email: string;
  phone: string;
  possuiWhatsapp?: boolean | null;
  status: 'active' | 'blocked';
  photo?: string | null;
  cargo?: 'SOCIO' | 'GERENTE' | 'CORRETOR';
}

interface Ticket {
  id: string;
  subject: string;
  requester: string;
  date: string;
  status: string;
}

interface AuditLogEntry {
  id: number;
  level?: string;
  event?: string;
  service?: string;
  createdAt?: string;
  data?: any;
}

type TagOption = { id: number; name: string };

interface RealEstateProfile {
  id: number;
  name: string;
  status: AgencyStatus;
  logo_initial: string;
  cvcrmId?: string | null;
  dashboard: DashboardMetrics;
  registration: RegistrationData;
  products: Array<{ id: number; name: string }>;
  team: TeamMember[];
  tickets: Ticket[];
}

const mapApiStatus = (status?: string, active?: boolean): AgencyStatus => resolveImobiliariaStatus(status, active);

const getWhatsappStatusMeta = (value?: boolean | null) => {
  if (value === true) return { label: 'WhatsApp valido', className: 'bg-green-500' };
  if (value === false) return { label: 'Sem WhatsApp', className: 'bg-red-500' };
  return { label: 'Validacao pendente', className: 'bg-yellow-400' };
};

const normalizeTag = (value: string) => value.replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
const normalizeTagKey = (value: string) => normalizeTag(value).toLowerCase();
const parseTags = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value.map((tag) => normalizeTag(String(tag))).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((tag) => normalizeTag(tag))
      .filter(Boolean);
  }
  return [];
};
const truncateText = (value: string, maxLength: number) => {
  const raw = value || '';
  if (raw.length <= maxLength) return raw;
  return `${raw.slice(0, maxLength - 3).trimEnd()}...`;
};

const getStatusBucket = (status: AgencyStatus): AgencyStatusBucket => {
  switch (status) {
    case 'EM_ANALISE':
      return 'analysis';
    case 'CREDENCIANDO':
      return 'credentialing';
    case 'APROVADA':
      return 'active';
    case 'REJEITADA':
    case 'SUSPENSA':
    default:
      return 'blocked';
  }
};

export const RealEstatePage: React.FC<RealEstatePageProps> = ({ darkMode, role, initialAgencyId, initialTabSlug }) => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'list' | 'detail'>(initialAgencyId ? 'detail' : 'list');
  const [activeTab, setActiveTab] = useState<RealEstateTab>(() => resolveTabFromSlug(initialTabSlug));
  const [registrationTab, setRegistrationTab] = useState<'empresa' | 'socio'>('empresa');
  
  // Data States
  const [agencies, setAgencies] = useState<RealEstateList[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<RealEstateProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState<string | null>(null);
  const [ticketsPage, setTicketsPage] = useState(1);
  const [attendanceTotal, setAttendanceTotal] = useState<number | null>(null);
  const ticketsPageSize = 15;
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [teamBrokenPhotoIds, setTeamBrokenPhotoIds] = useState<Set<number>>(new Set());
  const [teamRefreshingPhotoIds, setTeamRefreshingPhotoIds] = useState<Set<number>>(new Set());
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [auditLogsError, setAuditLogsError] = useState<string | null>(null);
  const [auditLogsOffset, setAuditLogsOffset] = useState(0);
  const [auditLogsHasMore, setAuditLogsHasMore] = useState(true);
  const [auditLogsLoadingMore, setAuditLogsLoadingMore] = useState(false);
  const auditLogsPageSize = 50;
  
  // List View Filtering
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | AgencyStatusBucket>('all');
  const [listSelectedTags, setListSelectedTags] = useState<string[]>([]);
  const [listTagsMode, setListTagsMode] = useState<'any' | 'all'>('any');
  const [listPage, setListPage] = useState(1);
  const listPageSize = 15;

  // Detail View Filtering
  const [teamSearch, setTeamSearch] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false);
  const [productsCatalog, setProductsCatalog] = useState<Array<{ id: number; name: string }>>([]);
  const [productsCatalogLoading, setProductsCatalogLoading] = useState(false);
  const [productsCatalogError, setProductsCatalogError] = useState<string | null>(null);
  const [productsSelection, setProductsSelection] = useState<number[]>([]);
  const [productsSaving, setProductsSaving] = useState(false);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const [instagramAvatarUrl, setInstagramAvatarUrl] = useState('');
  const [editForm, setEditForm] = useState({
    fantasy_name: '',
    corporate_name: '',
    cnpj: '',
    creci_pj: '',
    address: '',
    neighborhood: '',
    zip: '',
    email: '',
    website: '',
    owner_name: '',
    owner_email: '',
    owner_whatsapp: '',
    instagram: '',
    facebook: '',
    linkedin: '',
  });
  const [cvcrmIdInput, setCvcrmIdInput] = useState('');
  const [cvcrmSaving, setCvcrmSaving] = useState(false);
  const [cvcrmCapturing, setCvcrmCapturing] = useState(false);
  const [cvcrmSyncing, setCvcrmSyncing] = useState(false);
  const [availableTags, setAvailableTags] = useState<TagOption[]>([]);
  const [agencyTags, setAgencyTags] = useState<TagOption[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [tagSaving, setTagSaving] = useState(false);
  const [tagError, setTagError] = useState<string | null>(null);
  const [tagSuccess, setTagSuccess] = useState<string | null>(null);
  const tagSnapshotRef = useRef<string[]>([]);
  const defaultTitleRef = useRef<string | null>(null);

  const fetchList = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${CORE_API}/imobiliarias`, { headers: { ...getAuthHeaders() } });
      if (!response.ok) throw new Error('Falha ao buscar imobiliarias');
      const data = await response.json();
      const mapped: RealEstateList[] = (data?.imobiliarias || []).map((item: any) => ({
        id: item.id,
        name: item.nomeFantasia || item.razaoSocial || 'Imobiliaria',
        corporateName: item.razaoSocial || item.razao_social || '-',
        status: mapApiStatus(item.status, item.ativa),
        phone: item.responsavelWhatsapp || '-',
        email: item.email || '-',
        tags: parseTags(item.tags),
      }));
      setAgencies(mapped);
    } catch (e) {
      console.error('Erro ao buscar imobiliarias', e);
      setAgencies([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // --- FETCH LIST ---
  useEffect(() => {
    if (viewMode === 'list') {
      fetchList();
    }
  }, [viewMode, fetchList]);

  useEffect(() => {
    if (viewMode !== 'list') return;
    fetchAvailableTags();
    setListSelectedTags([]);
    setListTagsMode('any');
    setListPage(1);
  }, [viewMode]);

  useEffect(() => {
    if (statusFilter === 'all') {
      setListSelectedTags([]);
      setListTagsMode('any');
    }
    setListPage(1);
  }, [statusFilter]);
  useEffect(() => {
    setListPage(1);
  }, [search, listSelectedTags, listTagsMode]);

  const loadTagData = async (imobiliariaId: number) => {
    try {
      const [tagsRes, agencyTagsRes] = await Promise.all([
        fetch(`${CORE_API}/imobiliarias/tags`, { headers: { ...getAuthHeaders() } }),
        fetch(`${CORE_API}/imobiliarias/${imobiliariaId}/tags`, { headers: { ...getAuthHeaders() } }),
      ]);
      const tagsPayload = tagsRes.ok ? await tagsRes.json() : {};
      const agencyPayload = agencyTagsRes.ok ? await agencyTagsRes.json() : {};
      const nextAvailable = Array.isArray(tagsPayload?.tags) ? tagsPayload.tags : [];
      const nextAgency = Array.isArray(agencyPayload?.tags) ? agencyPayload.tags : [];
      setAvailableTags(nextAvailable);
      setAgencyTags(nextAgency);
      tagSnapshotRef.current = nextAgency.map((tag: TagOption) => normalizeTagKey(tag.name)).sort();
    } catch (err) {
      console.error('Erro ao carregar tags da imobiliaria', err);
      setAvailableTags([]);
      setAgencyTags([]);
      tagSnapshotRef.current = [];
    }
  };

  const fetchAvailableTags = async () => {
    try {
      const tagsRes = await fetch(`${CORE_API}/imobiliarias/tags`, { headers: { ...getAuthHeaders() } });
      const tagsPayload = tagsRes.ok ? await tagsRes.json() : {};
      const nextAvailable = Array.isArray(tagsPayload?.tags) ? tagsPayload.tags : [];
      setAvailableTags(nextAvailable);
    } catch (err) {
      console.error('Erro ao carregar tags', err);
      setAvailableTags([]);
    }
  };
  // --- FETCH PROFILE ---
  const handleSelectAgency = async (id: number, pushRoute?: boolean) => {
    setSelectedProfile(null);
    setViewMode('detail');
    setIsLoading(true);
    setProfileError(null);
    setTagError(null);
    setTagSuccess(null);
    try {
      const [imobRes, teamRes, productsRes] = await Promise.all([
        fetch(`${CORE_API}/imobiliarias/${id}`, { headers: { ...getAuthHeaders() } }),
        fetch(`${CORE_API}/imobiliarias/${id}/corretores`, { headers: { ...getAuthHeaders() } }),
        fetch(`${CORE_API}/imobiliarias/${id}/produtos`, { headers: { ...getAuthHeaders() } })
      ]);
      if (!imobRes.ok) throw new Error('Falha ao buscar imobiliária');
      const data = await imobRes.json();
      const imob = data?.imobiliaria;
      if (!imob) throw new Error('Imobiliária não encontrada');

      let teamList: TeamMember[] = [];
      if (teamRes.ok) {
        const teamData = await teamRes.json();
        teamList = (teamData?.corretores || []).map((c: any) => ({
          id: c.id,
          name: c.nome || 'Corretor',
          role: c.cargo === 'GERENTE' ? 'Gestor' : c.cargo === 'SOCIO' ? 'Sócio' : c.ehGerente ? 'Gestor' : c.ehSocio ? 'Sócio' : 'Corretor',
          email: c.email || '-',
          phone: c.telefone || '-',
          possuiWhatsapp: c.possuiWhatsapp ?? c.possui_whatsapp ?? null,
          status: c.ativo ? 'active' : 'blocked',
          photo: c.fotoUrl || c.foto_url || null,
          cargo: c.cargo,
        }));
      }

      let productList: Array<{ id: number; name: string }> = [];
      if (productsRes.ok) {
        const productsData = await productsRes.json();
        productList = (productsData?.produtos || []).map((p: any) => ({
          id: p.id,
          name: p.nome || `Produto ${p.id}`
        }));
      }

      const profile: RealEstateProfile = {
        id: imob.id,
        name: imob.nomeFantasia || imob.razaoSocial || 'Imobiliária',
        status: mapApiStatus(imob.status, imob.ativa),
        logo_initial: imob.logoInicial || (imob.nomeFantasia || '?').slice(0, 2).toUpperCase(),
        cvcrmId: imob.cvcrmId || imob.cvcrm_id || null,
        dashboard: { total_sales: 'R$ 0', active_leads: 0, conversion_rate: '0%', ranking_position: 0 },
        registration: {
          fantasy_name: imob.nomeFantasia || '-',
          corporate_name: imob.razaoSocial || '-',
          cnpj: imob.cnpj || '-',
          creci_pj: imob.creciPj || '-',
          address: imob.endereco || '-',
          neighborhood: imob.bairro || '-',
          zip: imob.cep || '-',
          email: imob.email || '-',
          website: imob.website || '',
          owner: {
            name: imob.responsavelNome || '-',
            email: imob.responsavelEmail || '-',
            whatsapp: imob.responsavelWhatsapp || '-',
          },
          socials: {
            instagram: imob.instagram || '',
            facebook: imob.facebook || '',
            linkedin: imob.linkedin || '',
          },
        },
        products: productList,
        team: teamList,
        tickets: [],
      };

      setSelectedProfile(profile);
      setAttendanceTotal(null);
      setTicketsError(null);
      setTeamBrokenPhotoIds(new Set());
      setTeamRefreshingPhotoIds(new Set());
      await loadTagData(id);
      if (pushRoute) {
        navigate(buildAgencyTabPath(id, activeTab), { replace: false });
      }
      setTeamSearch(''); // Reset team search
    } catch (e) {
      console.error("Erro ao buscar perfil", e);
      setProfileError('Nao foi possivel carregar o perfil da imobiliaria.');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load navigation (deep link)
  useEffect(() => {
    if (initialAgencyId) {
      handleSelectAgency(initialAgencyId);
    }
  }, [initialAgencyId]);

  useEffect(() => {
    setCvcrmIdInput(selectedProfile?.cvcrmId || '');
  }, [selectedProfile?.cvcrmId]);

  useEffect(() => {
    if (viewMode !== 'detail' || activeTab !== 'tickets' || !selectedProfile?.id) return;
    setTicketsPage(1);
    const controller = new AbortController();
    const fetchTickets = async () => {
      setTicketsLoading(true);
      setTicketsError(null);
      try {
        const params = new URLSearchParams({
          limit: '500',
          offset: '0',
          statuses: 'aguardando_atendimento,em_atendimento,aguardando_resposta_corretor,encerrado,descartado',
          imobiliariaId: String(selectedProfile.id)
        });
        const res = await fetch(`${CORE_API}/atendimentos/list?${params.toString()}`, {
          headers: { ...getAuthHeaders() },
          signal: controller.signal
        });
        if (!res.ok) throw new Error('Falha ao carregar atendimentos da imobiliaria');
        const data = await res.json();
        const total = Number(data?.total);
        const mappedTickets: Ticket[] = (data?.atendimentos || []).map((item: any) => ({
          id: String(item.id ?? ''),
          subject: item.categoria_nome || item.categoriaNome || item.categoria || `Atendimento #${item.id}`,
          requester: item.contato_nome || item.contatoNome || item.contato_whatsapp || item.contatoWhatsapp || '-',
          date: formatTimelineDate(item.created_at || item.createdAt || item.updated_at || item.updatedAt) || '-',
          status: item.status || '-'
        }));
        setAttendanceTotal(Number.isFinite(total) ? total : mappedTickets.length);
        setSelectedProfile(prev => (prev ? { ...prev, tickets: mappedTickets } : prev));
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        console.error('Erro ao carregar atendimentos da imobiliaria', err);
        setSelectedProfile(prev => (prev ? { ...prev, tickets: [] } : prev));
        setTicketsError('Nao foi possivel carregar os atendimentos desta imobiliaria.');
        setAttendanceTotal(0);
      } finally {
        setTicketsLoading(false);
      }
    };
    fetchTickets();
    return () => controller.abort();
  }, [viewMode, activeTab, selectedProfile?.id]);

  useEffect(() => {
    if (viewMode !== 'detail' || !selectedProfile?.id) return;
    const controller = new AbortController();
    const fetchAttendanceTotal = async () => {
      try {
        const params = new URLSearchParams({
          limit: '1',
          offset: '0',
          statuses: 'aguardando_atendimento,em_atendimento,aguardando_resposta_corretor,encerrado,descartado',
          imobiliariaId: String(selectedProfile.id)
        });
        const res = await fetch(`${CORE_API}/atendimentos/list?${params.toString()}`, {
          headers: { ...getAuthHeaders() },
          signal: controller.signal
        });
        if (!res.ok) throw new Error('Falha ao carregar total de atendimentos');
        const data = await res.json();
        const total = Number(data?.total);
        if (Number.isFinite(total)) {
          setAttendanceTotal(total);
        } else {
          setAttendanceTotal((data?.atendimentos || []).length);
        }
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        console.error('Erro ao carregar total de atendimentos da imobiliaria', err);
        setAttendanceTotal(0);
      }
    };
    fetchAttendanceTotal();
    return () => controller.abort();
  }, [viewMode, selectedProfile?.id]);

  const tagSuggestions = useMemo(() => {
    const query = normalizeTag(tagInput);
    if (!query) return [];
    const selected = new Set(agencyTags.map((tag) => normalizeTagKey(tag.name)));
    return availableTags
      .filter((tag) => !selected.has(normalizeTagKey(tag.name)))
      .filter((tag) => normalizeTagKey(tag.name).includes(normalizeTagKey(query)))
      .slice(0, 8);
  }, [availableTags, agencyTags, tagInput]);

  const isTagsDirty = useMemo(() => {
    const current = agencyTags.map((tag) => normalizeTagKey(tag.name)).sort();
    return JSON.stringify(current) !== JSON.stringify(tagSnapshotRef.current);
  }, [agencyTags]);

  const handleAddTag = (raw: string) => {
    const name = normalizeTag(raw);
    if (!name) return;
    const key = normalizeTagKey(name);
    if (agencyTags.some((tag) => normalizeTagKey(tag.name) === key)) {
      setTagInput('');
      return;
    }
    const existing = availableTags.find((tag) => normalizeTagKey(tag.name) === key);
    setAgencyTags((current) => [...current, existing || { id: 0, name }]);
    setTagInput('');
    setTagSuccess(null);
  };

  const handleRemoveTag = (name: string) => {
    const key = normalizeTagKey(name);
    setAgencyTags((current) => current.filter((tag) => normalizeTagKey(tag.name) !== key));
    setTagSuccess(null);
  };

  const handleSaveTags = async () => {
    if (!selectedProfile?.id) return;
    setTagSaving(true);
    setTagError(null);
    setTagSuccess(null);
    try {
      const payload = { tags: agencyTags.map((tag) => tag.name) };
      const res = await fetch(`${CORE_API}/imobiliarias/${selectedProfile.id}/tags`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Falha ao salvar tags');
      }
      await loadTagData(selectedProfile.id);
      setTagSuccess('Tags atualizadas.');
    } catch (err: any) {
      console.error('Erro ao salvar tags', err);
      setTagError(err?.message || 'Falha ao salvar tags');
    } finally {
      setTagSaving(false);
    }
  };

  useEffect(() => {
    setAvatarLoadFailed(false);
    setInstagramAvatarUrl('');
    const instagramValue = selectedProfile?.registration.socials.instagram || '';
    const username = getInstagramUsername(instagramValue);
    if (!username) return;
    const controller = new AbortController();
    const loadAvatar = async () => {
      try {
        const res = await fetch(`https://r.jina.ai/http://www.instagram.com/${username}/`, {
          signal: controller.signal,
        });
        if (!res.ok) return;
        const html = await res.text();
        const ogMatch =
          html.match(/property="og:image" content="([^"]+)"/i) ||
          html.match(/property='og:image' content='([^']+)'/i);
        if (!ogMatch?.[1]) return;
        setInstagramAvatarUrl(ogMatch[1].replace(/&amp;/g, '&'));
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
      }
    };
    loadAvatar();
    return () => controller.abort();
  }, [selectedProfile?.registration.socials.instagram]);
  
  useEffect(() => {
    const nextTab = resolveTabFromSlug(initialTabSlug);
    setActiveTab(prev => (prev === nextTab ? prev : nextTab));
  }, [initialTabSlug]);

  const handleTabChange = (tab: RealEstateTab) => {
    setActiveTab(tab);
    if (viewMode !== 'detail' || !selectedProfile?.id) return;
    navigate(buildAgencyTabPath(selectedProfile.id, tab), { replace: true });
  };

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!defaultTitleRef.current) {
      defaultTitleRef.current = document.title;
    }
    return () => {
      if (defaultTitleRef.current) {
        document.title = defaultTitleRef.current;
      }
    };
  }, []);

  const formatAgencyTitle = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return '';
    const maxLength = 32;
    if (trimmed.length <= maxLength) return trimmed;
    return `${trimmed.slice(0, maxLength - 3).trimEnd()}...`;
  };

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (viewMode === 'detail' && selectedProfile?.name) {
      const nextTitle = formatAgencyTitle(selectedProfile.name);
      if (nextTitle) {
        document.title = `IMB | ${nextTitle}`;
      }
      return;
    }
    if (defaultTitleRef.current) {
      document.title = defaultTitleRef.current;
    }
  }, [viewMode, selectedProfile?.name]);

  const loadAuditLogs = async (imobiliariaId: number, options?: { reset?: boolean; signal?: AbortSignal }) => {
    const reset = !!options?.reset;
    const offset = reset ? 0 : auditLogsOffset;
    if (reset) {
      setAuditLogsLoading(true);
      setAuditLogsError(null);
      setAuditLogsHasMore(true);
    } else {
      setAuditLogsLoadingMore(true);
    }
    try {
      const res = await fetch(
        `${CORE_API}/imobiliarias/${imobiliariaId}/logs?limit=${auditLogsPageSize}&offset=${offset}`,
        {
          headers: { ...getAuthHeaders() },
          signal: options?.signal,
        }
      );
      if (!res.ok) {
        throw new Error('Falha ao buscar logs');
      }
      const data = await res.json();
      const logs = Array.isArray(data?.logs) ? data.logs : [];
      setAuditLogs(prev => (reset ? logs : [...prev, ...logs]));
      setAuditLogsHasMore(logs.length === auditLogsPageSize);
      setAuditLogsOffset(offset + logs.length);
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      if (reset) {
        setAuditLogs([]);
      }
      setAuditLogsError('Não foi possível carregar a linha do tempo.');
    } finally {
      if (reset) {
        setAuditLogsLoading(false);
      } else {
        setAuditLogsLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    if (!selectedProfile?.id) return;
    const controller = new AbortController();
    loadAuditLogs(selectedProfile.id, { reset: true, signal: controller.signal });
    return () => controller.abort();
  }, [selectedProfile?.id]);

  const handleAuditScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (auditLogsLoading || auditLogsLoadingMore || !auditLogsHasMore) return;
    if (!selectedProfile?.id) return;
    const target = event.currentTarget;
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining <= 120) {
      loadAuditLogs(selectedProfile.id);
    }
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedProfile(null);
    navigate('/imobiliarias');
  };

  const handleToggleStatusFilter = (status: AgencyStatusBucket) => {
    setStatusFilter(prev => (prev === status ? 'all' : status));
  };

  const buildStatusPayload = (status: AgencyStatus) => ({
    status,
    ativa: status === 'APROVADA',
  });

  const updateAgencyStatus = async (agencyId: number, next: AgencyStatus) => {
    const payload = buildStatusPayload(next);
    try {
      const res = await fetch(`${CORE_API}/imobiliarias/${agencyId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Falha ao atualizar status');
      setSelectedProfile(prev => (prev ? { ...prev, status: next } : prev));
      setAgencies(prev => prev.map(a => (a.id === agencyId ? { ...a, status: next } : a)));
      await loadAuditLogs(agencyId, { reset: true });
    } catch (err) {
      console.error('Erro ao atualizar status da imobiliária', err);
      Swal.fire('Erro', 'Não foi possível atualizar o status.', 'error');
    }
  };

  const handleToggleAccess = async () => {
    if (!selectedProfile) return;
    const isBlocked = ['SUSPENSA', 'REJEITADA'].includes(selectedProfile.status);
    const nextStatus: AgencyStatus = isBlocked ? 'EM_ANALISE' : 'SUSPENSA';
    const { isConfirmed } = await Swal.fire({
      title: isBlocked ? 'Reativar acesso?' : 'Suspender acesso?',
      text: isBlocked
        ? 'A imobiliária voltará a ficar em análise.'
        : 'A imobiliária ficará com acesso suspenso.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: isBlocked ? 'Sim, reativar' : 'Sim, suspender',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: isBlocked ? '#2d6a2d' : '#8a1f1f',
    });
    if (!isConfirmed) return;
    await updateAgencyStatus(selectedProfile.id, nextStatus);
  };

  const handleSoftDelete = async () => {
    if (!selectedProfile) return;
    const { isConfirmed } = await Swal.fire({
      title: 'Excluir imobiliária?',
      text: 'Ela não aparecerá mais na lista nem entrará nas contagens.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#8a1f1f',
    });
    if (!isConfirmed) return;
    try {
      const res = await fetch(`${CORE_API}/imobiliarias/${selectedProfile.id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error('Falha ao excluir imobiliária');
      setAgencies((prev) => prev.filter((a) => a.id !== selectedProfile.id));
      setSelectedProfile(null);
      setViewMode('list');
      navigate('/imobiliarias');
      Swal.fire('Excluída', 'Imobiliária removida da lista.', 'success');
    } catch (err) {
      console.error('Erro ao excluir imobiliária', err);
      Swal.fire('Erro', 'Não foi possível excluir a imobiliária.', 'error');
    }
  };

  const handleStepperStatusChange = async (next: AgencyStatus) => {
    if (!selectedProfile || selectedProfile.status === next) return;
    const statusLabel =
      next === 'EM_ANALISE' ? 'Analise' : next === 'CREDENCIANDO' ? 'Credenciando' : 'Parceiro';
    const { isConfirmed } = await Swal.fire({
      title: 'Confirmar alteração?',
      text: `Alterar status para "${statusLabel}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, alterar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#294b29',
    });
    if (!isConfirmed) return;
    await updateAgencyStatus(selectedProfile.id, next);
  };

  const isCvcrmDirty = (cvcrmIdInput || '').trim() !== ((selectedProfile?.cvcrmId || '') as string).trim();

  const handleSaveCvcrmId = async () => {
    if (!selectedProfile) return;
    setCvcrmSaving(true);
    try {
      const payload = { cvcrmId: cvcrmIdInput.trim() || null };
      const res = await fetch(`${CORE_API}/imobiliarias/${selectedProfile.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Falha ao salvar CV ID');
      const data = await res.json();
      const nextCvcrmId = data?.imobiliaria?.cvcrmId ?? payload.cvcrmId ?? null;
      setSelectedProfile(prev => (prev ? { ...prev, cvcrmId: nextCvcrmId } : prev));
      Swal.fire('Salvo', 'CV ID atualizado com sucesso.', 'success');
    } catch (err) {
      console.error('Erro ao salvar CV ID', err);
      Swal.fire('Erro', 'Não foi possível salvar o CV ID.', 'error');
    } finally {
      setCvcrmSaving(false);
    }
  };

  const handleCaptureCvcrmId = async () => {
    if (!selectedProfile) return;
    setCvcrmCapturing(true);
    try {
      const res = await fetch(`${CORE_API}/imobiliarias/${selectedProfile.id}/capturar-cvcrm-id`, {
        method: 'POST',
        headers: { ...getAuthHeaders() },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.error) {
        throw new Error(data?.error || 'Falha ao capturar CV ID');
      }

      const nextCvcrmId = (data?.cvcrmId || data?.imobiliaria?.cvcrmId || '').toString().trim();
      if (!nextCvcrmId) {
        throw new Error('CV ID nao retornado.');
      }

      setCvcrmIdInput(nextCvcrmId);
      setSelectedProfile(prev => (prev ? { ...prev, cvcrmId: nextCvcrmId } : prev));
      Swal.fire('Capturado', `CV ID ${nextCvcrmId} salvo com sucesso.`, 'success');
    } catch (err: any) {
      console.error('Erro ao capturar CV ID', err);
      Swal.fire('Erro', err?.message || 'Nao foi possivel capturar o CV ID.', 'error');
    } finally {
      setCvcrmCapturing(false);
    }
  };

  const handleSyncCorretores = async () => {
    if (!selectedProfile) return;
    const cvcrmId = cvcrmIdInput.trim();
    if (!cvcrmId) {
      Swal.fire('Atenção', 'Informe o CV ID antes de sincronizar.', 'warning');
      return;
    }
    setCvcrmSyncing(true);
    try {
      const res = await fetch(`${CORE_API}/imobiliarias/${selectedProfile.id}/sync-corretores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ cvcrmId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.error) {
        throw new Error(data?.error || 'Falha ao sincronizar corretores');
      }

      const jobId = data?.jobId as string | undefined;
      if (!jobId) {
        throw new Error('Job de sincronizacao nao retornado.');
      }

      Swal.fire(
        'Sincronizacao iniciada',
        'O processo foi iniciado e pode levar alguns minutos.',
        'info'
      );

      const maxAttempts = 60;
      let finalData: any = null;
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        const statusRes = await fetch(
          `${CORE_API}/imobiliarias/${selectedProfile.id}/sync-corretores/jobs/${jobId}`,
          { headers: { ...getAuthHeaders() } }
        );
        const statusData = await statusRes.json().catch(() => ({}));
        if (!statusRes.ok || statusData?.error) {
          throw new Error(statusData?.error || 'Falha ao consultar status da sincronizacao');
        }
        console.info('Sync corretores status', {
          status: statusData?.status,
          processed: statusData?.result?.processed ?? null,
          saved: statusData?.result?.saved ?? null,
          whatsapp: statusData?.result?.whatsapp ?? null
        });
        if (statusData?.status === 'success') {
          finalData = statusData;
          break;
        }
        if (statusData?.status === 'error') {
          throw new Error(statusData?.error || 'Falha ao sincronizar corretores');
        }
      }

      if (!finalData) {
        throw new Error('Tempo limite aguardando sincronizacao');
      }

      const sync = finalData?.result || {};
      if (sync?.whatsapp) {
        console.info('WhatsApp validation stats', sync.whatsapp);
      }
      Swal.fire(
        'Sincronizacao concluida',
        `Processados ${sync?.processed || 0} corretores (${sync?.saved || 0} salvos).`,
        'success'
      );
      setSelectedProfile(prev => (prev ? { ...prev, cvcrmId } : prev));
    } catch (err: any) {
      console.error('Erro ao sincronizar corretores', err);
      Swal.fire('Erro', err?.message || 'Não foi possível sincronizar os corretores.', 'error');
    } finally {
      setCvcrmSyncing(false);
    }
  };

  const handleRefreshTeamPhoto = async (member: TeamMember) => {
    if (!selectedProfile) return;
    if (teamRefreshingPhotoIds.has(member.id)) return;
    setTeamRefreshingPhotoIds((prev) => {
      const next = new Set(prev);
      next.add(member.id);
      return next;
    });
    try {
      const res = await fetch(`${CORE_API}/corretores/${member.id}/refresh-foto`, {
        method: 'POST',
        headers: { ...getAuthHeaders() },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.fotoUrl) {
        setSelectedProfile(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            team: prev.team.map((item) => item.id === member.id ? { ...item, photo: data.fotoUrl } : item)
          };
        });
        setTeamBrokenPhotoIds((prev) => {
          const next = new Set(prev);
          next.delete(member.id);
          return next;
        });
      } else {
        setTeamBrokenPhotoIds((prev) => {
          const next = new Set(prev);
          next.add(member.id);
          return next;
        });
      }
    } catch (err) {
      setTeamBrokenPhotoIds((prev) => {
        const next = new Set(prev);
        next.add(member.id);
        return next;
      });
    } finally {
      setTeamRefreshingPhotoIds((prev) => {
        const next = new Set(prev);
        next.delete(member.id);
        return next;
      });
    }
  };

  const handleRefreshTeam = async () => {
    if (!selectedProfile) return;
    await handleSelectAgency(selectedProfile.id);
  };

  // Helpers for external links
  const openGoogleMaps = (address: string) => {
    const encoded = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank');
  };

const getExternalUrl = (url: string) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `https://${url}`;
};

const getInstagramUsername = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  const match = trimmed.match(/instagram\.com\/([^/?#]+)/i);
  if (!match) return '';
  return match[1].trim();
};

const openWebsite = (url: string) => {
  const href = getExternalUrl(url);
  if (!href) return;
  window.open(href, '_blank');
};

  const formatTimelineDate = (value?: string) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  };

  const formatCnpj = (value?: string) => {
    if (!value) return '-';
    const digits = value.replace(/\D/g, '');
    if (digits.length !== 14) return value;
    return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const maskCnpjInput = (value: string) => {
    return value
      .replace(/\D/g, '')
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .substring(0, 18);
  };

  const maskCepInput = (value: string) => {
    const digits = value.replace(/\D/g, '').substring(0, 8);
    if (digits.length <= 5) return digits;
    return digits.replace(/^(\d{5})(\d{1,3})$/, '$1-$2');
  };

  const maskWhatsappInput = (value: string) => {
    let digits = value.replace(/\D/g, '');
    digits = digits.substring(0, 11);
    if (digits.length > 10) {
      return digits.replace(/^(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    if (digits.length > 5) {
      return digits.replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    }
    if (digits.length > 2) {
      return digits.replace(/^(\d{2})(\d{0,5})/, '($1) $2');
    }
    return digits.replace(/^(\d*)/, '($1');
  };

  const getAuditResponsavelLabel = (responsavel?: { nome?: string; email?: string; tipo?: string }) => {
    if (!responsavel) return '';
    if (responsavel.nome) return responsavel.nome;
    if (responsavel.email) return responsavel.email;
    if (responsavel.tipo) return responsavel.tipo;
    return '';
  };

  const formatAuditValue = (key: string, value: any) => {
    if (value === null || value === undefined || value === '') return '-';
    if (key === 'cnpj') return formatCnpj(String(value));
    if (key === 'status') return getStatusLabel(String(value));
    if (key === 'ativa') return value ? 'Sim' : 'Não';
    return String(value);
  };

  const getAuditFieldLabel = (key: string) => {
    const labels: Record<string, string> = {
      nome_fantasia: 'Nome fantasia',
      razao_social: 'Razão social',
      cnpj: 'CNPJ',
      creci_pj: 'CRECI PJ',
      endereco: 'Endereço',
      bairro: 'Bairro',
      cidade: 'Cidade',
      estado: 'Estado',
      cep: 'CEP',
      email: 'Email',
      website: 'Website',
      responsavel_nome: 'Sócio responsável',
      responsavel_email: 'Email responsável',
      responsavel_whatsapp: 'WhatsApp responsável',
      instagram: 'Instagram',
      facebook: 'Facebook',
      linkedin: 'LinkedIn',
      logo_url: 'Logo',
      logo_inicial: 'Logo inicial',
      status: 'Status',
      ativa: 'Ativa'
    };
    return labels[key] || key;
  };

  const describeAuditEvent = (entry: AuditLogEntry) => {
    const event = entry.event || entry.data?.event || '';
    const changes = entry.data?.changes || {};
    const previous = entry.data?.previous || {};
    const responsavelLabel = getAuditResponsavelLabel(entry.data?.responsavel);
    const responsavelSuffix = responsavelLabel ? ` · por ${responsavelLabel}` : '';
    const responsavelDetail = responsavelSuffix ? `Alteração${responsavelSuffix}.` : '';
    const withResponsavelDetail = (detail: string, detailLines?: string[]) => {
      if (!responsavelDetail) {
        return { detail, detailLines, detailStrong: false };
      }
      return {
        detail: responsavelDetail,
        detailLines: detailLines?.length ? detailLines : [detail],
        detailStrong: true,
      };
    };
    switch (event) {
      case 'imobiliaria.create':
        return {
          title: 'Cadastro criado',
          ...withResponsavelDetail('Imobiliária registrada no sistema.'),
        };
      case 'imobiliaria.update': {
        if (Object.prototype.hasOwnProperty.call(changes, 'status')) {
          const fromStatus = getStatusLabel(String(previous?.status || '-'));
          const toStatus = getStatusLabel(String(changes?.status || '-'));
          const lines = [`Status: ${fromStatus} → ${toStatus}`];
          return {
            title: 'Status atualizado',
            ...withResponsavelDetail(`Novo status: ${toStatus}.`, lines),
          };
        }
        if (changes && Object.keys(changes).length) {
          const lines = Object.keys(changes).map((key) => {
            const label = getAuditFieldLabel(key);
            const fromValue = formatAuditValue(key, previous[key]);
            const toValue = formatAuditValue(key, changes[key]);
            return `${label}: ${fromValue} → ${toValue}`;
          });
          return {
            title: 'Dados atualizados',
            ...withResponsavelDetail('Informações da imobiliária foram alteradas.', lines),
          };
        }
        return {
          title: 'Dados atualizados',
          ...withResponsavelDetail('Informações da imobiliária foram alteradas.'),
        };
      }
      case 'imobiliaria.status': {
        const fromStatus = getStatusLabel(String(previous?.status || '-'));
        const toStatus = getStatusLabel(String(changes?.status || '-'));
        const lines = fromStatus || toStatus ? [`Status: ${fromStatus} → ${toStatus}`] : undefined;
        return {
          title: 'Status atualizado',
          ...withResponsavelDetail(`Novo status: ${toStatus}.`, lines),
        };
      }
      case 'imobiliaria.delete':
        return {
          title: 'Imobiliária excluída',
          ...withResponsavelDetail('Removida das listas e contagens.'),
        };
      case 'imobiliaria.suspend':
        return {
          title: 'Acesso suspenso',
          ...withResponsavelDetail('Imobiliária foi suspensa.'),
        };
      case 'imobiliaria.produto.add':
        return {
          title: 'Produto vinculado',
          ...withResponsavelDetail(`Produto ID: ${changes.produtoId || '-'}.`),
        };
      case 'imobiliaria.produto.remove':
        return {
          title: 'Produto removido',
          ...withResponsavelDetail(`Produto ID: ${changes.produtoId || '-'}.`),
        };
      case 'imobiliaria.corretor.link':
        return {
          title: 'Corretor vinculado',
          ...withResponsavelDetail(`Corretor ID: ${changes.corretorId || '-'}.`),
        };
      case 'imobiliaria.corretor.unlink':
        return {
          title: 'Corretor desvinculado',
          ...withResponsavelDetail(`Corretor ID: ${changes.corretorId || '-'}.`),
        };
      default:
        return {
          title: event || 'Evento',
          ...withResponsavelDetail(entry.data?.summary || 'Registro de auditoria.'),
        };
    }
  };

  const toggleListTag = useCallback((name: string) => {
    setListSelectedTags((current) => {
      if (current.includes(name)) {
        return current.filter((tag) => tag !== name);
      }
      return [...current, name];
    });
  }, []);
  const clearListTags = useCallback(() => {
    setListSelectedTags([]);
  }, []);

  const statusFilteredAgencies = useMemo(
    () => agencies.filter((agency) => statusFilter === 'all' || getStatusBucket(agency.status) === statusFilter),
    [agencies, statusFilter]
  );

  const filteredAgencies = useMemo(() => {
    const selectedTags = listSelectedTags.map((tag) => normalizeTagKey(tag));
    return statusFilteredAgencies.filter((agency) => {
      if (selectedTags.length > 0) {
        const agencyTags = (agency.tags || []).map((tag) => normalizeTagKey(tag));
        if (!agencyTags.length) return false;
        const matchAny = selectedTags.some((tag) => agencyTags.includes(tag));
        const matchAll = selectedTags.every((tag) => agencyTags.includes(tag));
        if (listTagsMode === 'all' && !matchAll) return false;
        if (listTagsMode === 'any' && !matchAny) return false;
      }
      return agency.name.toLowerCase().includes(search.toLowerCase());
    });
  }, [listSelectedTags, listTagsMode, search, statusFilteredAgencies]);
  const listTotalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredAgencies.length / listPageSize));
  }, [filteredAgencies.length]);
  const pagedAgencies = useMemo(() => {
    const start = (listPage - 1) * listPageSize;
    return filteredAgencies.slice(start, start + listPageSize);
  }, [filteredAgencies, listPage]);
  useEffect(() => {
    if (listPage > listTotalPages) {
      setListPage(listTotalPages);
    }
  }, [listPage, listTotalPages]);
  const listTagCount = useMemo(() => {
    if (!listSelectedTags.length) return 0;
    const selectedTags = listSelectedTags.map((tag) => normalizeTagKey(tag));
    return statusFilteredAgencies.filter((agency) => {
      const agencyTags = (agency.tags || []).map((tag) => normalizeTagKey(tag));
      if (!agencyTags.length) return false;
      const matchAny = selectedTags.some((tag) => agencyTags.includes(tag));
      const matchAll = selectedTags.every((tag) => agencyTags.includes(tag));
      return listTagsMode === 'all' ? matchAll : matchAny;
    }).length;
  }, [listSelectedTags, listTagsMode, statusFilteredAgencies]);

  const funnelCounts = useMemo(() => {
    const counts = { analysis: 0, credentialing: 0, active: 0, blocked: 0, total: agencies.length };
    agencies.forEach((agency) => {
      const bucket = getStatusBucket(agency.status);
      counts[bucket] = counts[bucket] + 1;
    });
    return counts;
  }, [agencies]);
const openEditModal = () => {
    if (!selectedProfile) return;
    setEditForm({
      fantasy_name: selectedProfile.registration.fantasy_name || '',
      corporate_name: selectedProfile.registration.corporate_name || '',
      cnpj: maskCnpjInput(selectedProfile.registration.cnpj || ''),
      creci_pj: selectedProfile.registration.creci_pj || '',
      address: selectedProfile.registration.address || '',
      neighborhood: selectedProfile.registration.neighborhood || '',
      zip: maskCepInput(selectedProfile.registration.zip || ''),
      email: selectedProfile.registration.email || '',
      website: selectedProfile.registration.website || '',
      owner_name: selectedProfile.registration.owner.name || '',
      owner_email: selectedProfile.registration.owner.email || '',
      owner_whatsapp: maskWhatsappInput(selectedProfile.registration.owner.whatsapp || ''),
      instagram: selectedProfile.registration.socials.instagram || '',
      facebook: selectedProfile.registration.socials.facebook || '',
      linkedin: selectedProfile.registration.socials.linkedin || '',
    });
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => setIsEditModalOpen(false);

  const openProductsModal = async () => {
    if (!selectedProfile) return;
    setProductsSelection(selectedProfile.products.map((product) => product.id));
    setIsProductsModalOpen(true);
    if (productsCatalog.length) return;
    setProductsCatalogLoading(true);
    setProductsCatalogError(null);
    try {
      const res = await fetch(`${CORE_API}/produtos?ativo=true`, { headers: { ...getAuthHeaders() } });
      if (!res.ok) throw new Error('Falha ao buscar produtos');
      const data = await res.json();
      const list = (data?.produtos || []).map((p: any) => ({ id: p.id, name: p.nome || `Produto ${p.id}` }));
      setProductsCatalog(list);
    } catch (err) {
      console.error('Erro ao buscar produtos', err);
      setProductsCatalog([]);
      setProductsCatalogError('Não foi possível carregar os produtos.');
    } finally {
      setProductsCatalogLoading(false);
    }
  };

  const closeProductsModal = () => setIsProductsModalOpen(false);

  const toggleProductSelection = (productId: number) => {
    setProductsSelection((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
    );
  };

  const handleSaveProducts = async () => {
    if (!selectedProfile) return;
    setProductsSaving(true);
    const currentIds = selectedProfile.products.map((product) => product.id);
    const toAdd = productsSelection.filter((id) => !currentIds.includes(id));
    const toRemove = currentIds.filter((id) => !productsSelection.includes(id));
    try {
      if (toAdd.length) {
        await Promise.all(
          toAdd.map((produtoId) =>
            fetch(`${CORE_API}/imobiliarias/${selectedProfile.id}/produtos`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
              body: JSON.stringify({ produtoId }),
            })
          )
        );
      }
      if (toRemove.length) {
        await Promise.all(
          toRemove.map((produtoId) =>
            fetch(`${CORE_API}/imobiliarias/${selectedProfile.id}/produtos/${produtoId}`, {
              method: 'DELETE',
              headers: { ...getAuthHeaders() },
            })
          )
        );
      }
      const nameById = new Map<number, string>();
      productsCatalog.forEach((product) => nameById.set(product.id, product.name));
      selectedProfile.products.forEach((product) => nameById.set(product.id, product.name));
      const nextProducts = productsSelection.map((id) => ({ id, name: nameById.get(id) || `Produto ${id}` }));
      setSelectedProfile((prev) => (prev ? { ...prev, products: nextProducts } : prev));
      setIsProductsModalOpen(false);
      if (role === 'admin') {
        await loadAuditLogs(selectedProfile.id, { reset: true });
      }
    } catch (err) {
      console.error('Erro ao atualizar produtos da imobiliária', err);
      Swal.fire('Erro', 'Não foi possível atualizar os produtos.', 'error');
    } finally {
      setProductsSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedProfile) return;
    try {
      const payload = {
        fantasyName: editForm.fantasy_name,
        corporateName: editForm.corporate_name,
        cnpj: editForm.cnpj,
        creci: editForm.creci_pj,
        address: editForm.address,
        neighborhood: editForm.neighborhood,
        zip: editForm.zip,
        email: editForm.email,
        website: editForm.website,
        ownerName: editForm.owner_name,
        ownerEmail: editForm.owner_email,
        ownerWhatsapp: editForm.owner_whatsapp,
        instagram: editForm.instagram,
        facebook: editForm.facebook,
        linkedin: editForm.linkedin,
      };

      const res = await fetch(`${CORE_API}/imobiliarias/${selectedProfile.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Falha ao salvar dados');
      const updated = await res.json();
      const imob = updated.imobiliaria || {};

      setSelectedProfile((prev) => prev ? {
        ...prev,
        name: imob.nomeFantasia || prev.name,
        registration: {
          fantasy_name: imob.nomeFantasia || editForm.fantasy_name,
          corporate_name: imob.razaoSocial || editForm.corporate_name,
          cnpj: imob.cnpj || editForm.cnpj,
          creci_pj: imob.creciPj || editForm.creci_pj,
          address: imob.endereco || editForm.address,
          neighborhood: imob.bairro || editForm.neighborhood,
          zip: imob.cep || editForm.zip,
          email: imob.email || editForm.email,
          website: imob.website || editForm.website,
          owner: {
            name: imob.responsavelNome || editForm.owner_name,
            email: imob.responsavelEmail || editForm.owner_email,
            whatsapp: imob.responsavelWhatsapp || editForm.owner_whatsapp,
          },
          socials: {
            instagram: imob.instagram || editForm.instagram,
            facebook: imob.facebook || editForm.facebook,
            linkedin: imob.linkedin || editForm.linkedin,
          },
        },
      } : prev);

      setAgencies((prev) => prev.map((a) => a.id === selectedProfile.id ? { ...a, name: imob.nomeFantasia || a.name, email: imob.email || a.email } : a));
      setIsEditModalOpen(false);
      Swal.fire('Sucesso', 'Dados atualizados.', 'success');
      if (role === 'admin') {
        await loadAuditLogs(selectedProfile.id, { reset: true });
      }
    } catch (err) {
      console.error('Erro ao salvar cadastro', err);
      Swal.fire('Erro', 'Não foi possível salvar os dados.', 'error');
    }
  };

  // Filter Team (Includes Phone Search)
  const filteredTeam = selectedProfile?.team.filter(member => 
    member.name.toLowerCase().includes(teamSearch.toLowerCase()) ||
    member.email.toLowerCase().includes(teamSearch.toLowerCase()) ||
    member.phone.includes(teamSearch)
  ) || [];

  // --- RENDER HELPERS ---
  const getStatusLabel = (status: string) => {
     switch(status) {
        case 'EM_ANALISE': return 'Em Analise';
        case 'CREDENCIANDO': return 'Credenciando';
        case 'APROVADA': return 'Aprovada';
        case 'REJEITADA': return 'Rejeitada';
        case 'SUSPENSA': return 'Suspensa';
        case 'active': return 'Ativo';
        case 'blocked': return 'Suspensa';
        case 'aguardando_atendimento': return 'Aguardando';
        case 'em_atendimento': return 'Em atendimento';
        case 'aguardando_resposta_corretor': return 'Aguardando corretor';
        case 'encerrado': return 'Encerrado';
        case 'descartado': return 'Descartado';
        default: return status;
     }
  };

  const getStatusColorClass = (status: string, isBadge = true) => {
    if (isBadge) {
       switch(status) {
          case 'EM_ANALISE': return darkMode ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-900' : 'bg-yellow-100 text-yellow-800 border border-yellow-200';
          case 'CREDENCIANDO': return darkMode ? 'bg-blue-900/30 text-blue-400 border border-blue-900' : 'bg-blue-100 text-blue-800 border border-blue-200';
          case 'APROVADA': return darkMode ? 'bg-green-900/30 text-green-400 border border-green-900' : 'bg-green-100 text-green-800 border border-green-200';
          case 'REJEITADA':
          case 'SUSPENSA': return darkMode ? 'bg-red-900/30 text-red-400 border border-red-900' : 'bg-red-100 text-red-800 border border-red-200';
          case 'active': return darkMode ? 'bg-green-900/30 text-green-400 border border-green-900' : 'bg-green-100 text-green-800 border border-green-200';
          case 'blocked': return darkMode ? 'bg-red-900/30 text-red-400 border border-red-900' : 'bg-red-100 text-red-800 border border-red-200';
          case 'aguardando_atendimento':
          case 'aguardando_resposta_corretor': return darkMode ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-900' : 'bg-yellow-100 text-yellow-800 border border-yellow-200';
          case 'em_atendimento': return darkMode ? 'bg-blue-900/30 text-blue-400 border border-blue-900' : 'bg-blue-100 text-blue-800 border border-blue-200';
          case 'encerrado':
          case 'descartado': return darkMode ? 'bg-gray-800 text-gray-300 border border-gray-700' : 'bg-gray-100 text-gray-700 border border-gray-200';
          default: return 'bg-gray-100 text-gray-800';
       }
    }
    return '';
  };

  const StatusBadge = ({ status }: { status: string }) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColorClass(status)}`}>
      {getStatusLabel(status)}
    </span>
  );

  // --- LIST VIEW ---
  if (viewMode === 'list') {
    return (
      <div className={`p-6 md:p-8 min-h-full ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
        <div className="flex flex-col md:flex-row justify-between mb-8 gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-vision-green'}`}>
              Parceiros Imobiliários
            </h1>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Consulte a rede de imobiliárias por região de atuação.
            </p>
          </div>
        </div>

        <div className={`rounded-xl border shadow-sm p-6 mb-6 ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wide flex items-center gap-2">
                <Filter size={16} /> Funil de parceria
              </h3>
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Clique para filtrar a lista abaixo por estágio.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
            <button
              onClick={() => setStatusFilter('all')}
              className={`
                p-3 rounded-lg text-left border transition-all active:scale-95
                ${statusFilter === 'all'
                  ? (darkMode ? 'bg-vision-activeRed text-white border-vision-activeRed shadow-md ring-2 ring-vision-activeRed/30' : 'bg-[#294b29] text-white border-[#294b29] shadow-md ring-2 ring-[#294b29]/30')
                  : (darkMode ? 'bg-vision-surface/70 border-vision-border text-gray-200 hover:bg-vision-surface' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100')
                }
              `}
              aria-label="Limpar filtros de status"
            >
              <span className={`block text-2xl font-bold ${statusFilter === 'all' ? 'text-white' : (darkMode ? 'text-white' : 'text-gray-900')}`}>
                {funnelCounts.total}
              </span>
              <span className={`text-[11px] font-bold uppercase ${statusFilter === 'all' ? 'text-white/90' : (darkMode ? 'text-gray-300' : 'text-gray-600')}`}>
                Total
              </span>
            </button>
            <button
              onClick={() => handleToggleStatusFilter('analysis')}
              className={`
                p-3 rounded-lg text-left border transition-all active:scale-95
                ${statusFilter === 'analysis'
                  ? 'bg-yellow-500 text-white border-yellow-600 shadow-md ring-2 ring-yellow-500/30'
                  : (darkMode ? 'bg-yellow-900/10 border-yellow-900/30 hover:bg-yellow-900/20' : 'bg-yellow-50 border-yellow-100 hover:bg-yellow-100')
                }
              `}
            >
              <span className={`block text-2xl font-bold ${statusFilter === 'analysis' ? 'text-white' : (darkMode ? 'text-yellow-400' : 'text-yellow-700')}`}>
                {funnelCounts.analysis}
              </span>
              <span className={`text-[11px] font-bold uppercase ${statusFilter === 'analysis' ? 'text-white/90' : (darkMode ? 'text-yellow-500' : 'text-yellow-700')}`}>
                Análise
              </span>
            </button>

            <button
              onClick={() => handleToggleStatusFilter('credentialing')}
              className={`
                p-3 rounded-lg text-left border transition-all active:scale-95
                ${statusFilter === 'credentialing'
                  ? 'bg-blue-600 text-white border-blue-700 shadow-md ring-2 ring-blue-500/30'
                  : (darkMode ? 'bg-blue-900/10 border-blue-900/30 hover:bg-blue-900/20' : 'bg-blue-50 border-blue-100 hover:bg-blue-100')
                }
              `}
            >
              <span className={`block text-2xl font-bold ${statusFilter === 'credentialing' ? 'text-white' : (darkMode ? 'text-blue-400' : 'text-blue-700')}`}>
                {funnelCounts.credentialing}
              </span>
              <span className={`text-[11px] font-bold uppercase ${statusFilter === 'credentialing' ? 'text-white/90' : (darkMode ? 'text-blue-500' : 'text-blue-700')}`}>
                Credenciando
              </span>
            </button>

            <button
              onClick={() => handleToggleStatusFilter('active')}
              className={`
                p-3 rounded-lg text-left border transition-all active:scale-95
                ${statusFilter === 'active'
                  ? 'bg-green-600 text-white border-green-700 shadow-md ring-2 ring-green-500/30'
                  : (darkMode ? 'bg-green-900/10 border-green-900/30 hover:bg-green-900/20' : 'bg-green-50 border-green-100 hover:bg-green-100')
                }
              `}
            >
              <span className={`block text-2xl font-bold ${statusFilter === 'active' ? 'text-white' : (darkMode ? 'text-green-400' : 'text-green-700')}`}>
                {funnelCounts.active}
              </span>
              <span className={`text-[11px] font-bold uppercase ${statusFilter === 'active' ? 'text-white/90' : (darkMode ? 'text-green-500' : 'text-green-700')}`}>
                Ativas
              </span>
            </button>

            <button
              onClick={() => handleToggleStatusFilter('blocked')}
              className={`
                p-3 rounded-lg text-left border transition-all active:scale-95
                ${statusFilter === 'blocked'
                  ? 'bg-red-600 text-white border-red-700 shadow-md ring-2 ring-red-500/30'
                  : (darkMode ? 'bg-red-900/10 border-red-900/30 hover:bg-red-900/20' : 'bg-red-50 border-red-100 hover:bg-red-100')
                }
              `}
            >
              <span className={`block text-2xl font-bold ${statusFilter === 'blocked' ? 'text-white' : (darkMode ? 'text-red-400' : 'text-red-700')}`}>
                {funnelCounts.blocked}
              </span>
              <span className={`text-[11px] font-bold uppercase ${statusFilter === 'blocked' ? 'text-white/90' : (darkMode ? 'text-red-500' : 'text-red-700')}`}>
                Suspensas
              </span>
            </button>
          </div>
          {statusFilter !== 'all' ? (
            <div className={`mt-4 rounded-lg border p-3 flex flex-col gap-2 ${darkMode ? 'bg-vision-surface/70 border-vision-border' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase opacity-70">
                  <Tag size={14} />
                  Tags
                  {listSelectedTags.length > 0 && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                      {listTagCount}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className={`flex rounded-full border overflow-hidden text-[10px] font-semibold ${darkMode ? 'border-vision-border' : 'border-gray-200'}`}>
                    <button
                      type="button"
                      onClick={() => setListTagsMode('any')}
                      className={`px-3 py-1 ${listTagsMode === 'any' ? (darkMode ? 'bg-vision-activeRed text-white' : 'bg-[#294b29] text-white') : (darkMode ? 'text-gray-300 hover:bg-vision-surface' : 'text-gray-600 hover:bg-gray-100')}`}
                    >
                      Qualquer
                    </button>
                    <button
                      type="button"
                      onClick={() => setListTagsMode('all')}
                      className={`px-3 py-1 ${listTagsMode === 'all' ? (darkMode ? 'bg-vision-activeRed text-white' : 'bg-[#294b29] text-white') : (darkMode ? 'text-gray-300 hover:bg-vision-surface' : 'text-gray-600 hover:bg-gray-100')}`}
                    >
                      Todas
                    </button>
                  </div>
                  {listSelectedTags.length > 0 && (
                    <button
                      type="button"
                      onClick={clearListTags}
                      className={`text-[10px] font-semibold px-2 py-1 rounded-full border ${darkMode ? 'border-vision-border text-gray-300 hover:bg-vision-surface' : 'border-gray-200 text-gray-600 hover:bg-gray-100'}`}
                    >
                      Limpar
                    </button>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {availableTags.length > 0 ? (
                  availableTags.map((tag) => {
                    const active = listSelectedTags.includes(tag.name);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => toggleListTag(tag.name)}
                        className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                          active
                            ? (darkMode ? 'bg-white text-gray-900 border-white' : 'bg-[#294b29] text-white border-[#294b29]')
                            : (darkMode ? 'border-vision-border text-gray-300 hover:bg-vision-surface' : 'border-gray-200 text-gray-600 hover:bg-gray-100')
                        }`}
                      >
                        {tag.name}
                      </button>
                    );
                  })
                ) : (
                  <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Nenhuma tag cadastrada.</span>
                )}
              </div>
            </div>
          ) : (
            <div className={`mt-4 rounded-lg border px-3 py-2 text-xs ${darkMode ? 'bg-vision-surface/70 border-vision-border text-gray-500' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
              Selecione um status para filtrar por tags.
            </div>
          )}
        </div>

        <div className={`rounded-xl border shadow-sm overflow-hidden flex flex-col ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
          <div className={`p-4 border-b flex flex-col gap-4 ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
             <div className="flex items-center gap-3">
               <div className="relative w-full">
                 <Search className={`absolute left-3 top-2.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} size={18} />
                 <input 
                    type="text" 
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar imobiliaria..." 
                    className={`
                      w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 outline-none text-sm transition-all
                      ${darkMode 
                        ? 'bg-vision-surface border-vision-border text-white focus:ring-gray-600' 
                        : 'bg-gray-50 border-gray-300 text-gray-800 focus:ring-[#294b29] focus:border-transparent'
                      }
                    `}
                 />
               </div>
               <button
                 type="button"
                 onClick={fetchList}
                 disabled={isLoading}
                 className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                   darkMode
                     ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                     : 'border-gray-200 text-gray-700 hover:bg-gray-100'
                 } ${isLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
               >
                 <RefreshCw size={14} />
                 Atualizar
               </button>
             </div>
          </div>

          {isLoading ? (
             <div className="p-12 flex flex-col items-center justify-center">
                <Loader2 className={`w-8 h-8 animate-spin ${darkMode ? 'text-white' : 'text-[#294b29]'}`} />
                <p className={`mt-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Carregando lista...</p>
             </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className={`text-xs uppercase tracking-wider ${darkMode ? 'bg-vision-surface text-gray-400' : 'bg-gray-50 text-gray-600'}`}>
                    <th className="px-6 py-4 font-semibold">Imobiliária</th>
                    <th className="px-6 py-4 font-semibold">Razao Social</th>
                    <th className="px-6 py-4 font-semibold text-center">Status</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-vision-border' : 'divide-gray-200'}`}>
                  {pagedAgencies.map((agency) => (
                    <tr 
                      key={agency.id} 
                      onClick={() => {
                        const base = typeof window !== 'undefined' ? window.location.origin : '';
                        const target = `${base}${buildAgencyTabPath(agency.id, 'dashboard')}`;
                        window.open(target, '_blank', 'noopener,noreferrer');
                      }}
                      className={`
                        transition-colors cursor-pointer group
                        ${darkMode ? 'hover:bg-vision-surface' : 'hover:bg-gray-50'}
                      `}
                    >
                      <td className="px-6 py-4">
                        <p className={`font-medium group-hover:underline ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                          {truncateText(agency.name, 36)}
                        </p>
                      </td>
                      <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {truncateText(agency.corporateName, 42)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <StatusBadge status={agency.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pagedAgencies.length === 0 && (
                <div className="p-12 text-center text-gray-500">Nenhuma imobiliária encontrada.</div>
              )}
            </div>
          )}
        </div>
        {filteredAgencies.length > 0 && (
          <div className="mt-4 flex items-center justify-between text-sm">
            <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Pagina {listPage} de {listTotalPages}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setListPage((current) => Math.max(1, current - 1))}
                disabled={listPage <= 1}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                  listPage <= 1
                    ? 'opacity-50 cursor-not-allowed'
                    : darkMode
                    ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-100'
                }`}
              >
                Anterior
              </button>
              <button
                type="button"
                onClick={() => setListPage((current) => Math.min(listTotalPages, current + 1))}
                disabled={listPage >= listTotalPages}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                  listPage >= listTotalPages
                    ? 'opacity-50 cursor-not-allowed'
                    : darkMode
                    ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-100'
                }`}
              >
                Proxima
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- DETAIL VIEW ---
  if (viewMode === 'detail' && selectedProfile) {
    // Stepper configuration
    const steps = [
        { id: 'EM_ANALISE', label: 'Análise', shortLabel: 'Aná', icon: FileSearch },
        { id: 'CREDENCIANDO', label: 'Credenciando', shortLabel: 'Cred', icon: PenTool },
        { id: 'APROVADA', label: 'Ativo', shortLabel: 'Ativ', icon: CheckCircle2 }
    ];

    const currentStepIndex = steps.findIndex(s => s.id === selectedProfile.status);
    const isBlocked = ['SUSPENSA', 'REJEITADA'].includes(selectedProfile.status);
    const socialLinks = [
      { id: 'instagram', label: 'Instagram', Icon: Instagram, value: selectedProfile.registration.socials.instagram },
      { id: 'facebook', label: 'Facebook', Icon: Facebook, value: selectedProfile.registration.socials.facebook },
      { id: 'linkedin', label: 'LinkedIn', Icon: Linkedin, value: selectedProfile.registration.socials.linkedin }
    ];
    const avatarUrl = avatarLoadFailed ? '' : instagramAvatarUrl;

    return (
      <div className={`p-6 md:p-8 min-h-full ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
         {isEditModalOpen && (
           <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
             <div className={`w-full max-w-3xl rounded-2xl shadow-2xl border ${darkMode ? 'bg-vision-darkGreen border-vision-border text-white' : 'bg-white border-gray-200 text-gray-800'}`}>
               <div className={`flex items-center justify-between px-6 py-4 border-b ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
                 <div>
                   <h3 className="text-lg font-bold">Editar dados cadastrais</h3>
                   <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{selectedProfile.name}</p>
                 </div>
                 <button onClick={closeEditModal} className="text-sm font-semibold opacity-70 hover:opacity-100">Fechar</button>
               </div>

               <div className="max-h-[70vh] overflow-y-auto p-6 space-y-6">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div>
                     <label className="text-xs font-bold uppercase opacity-70">Nome Fantasia</label>
                     <input
                       className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}
                       value={editForm.fantasy_name}
                       onChange={(e) => setEditForm({ ...editForm, fantasy_name: e.target.value })}
                     />
                   </div>
                   <div>
                     <label className="text-xs font-bold uppercase opacity-70">Razão Social</label>
                     <input
                       className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}
                       value={editForm.corporate_name}
                       onChange={(e) => setEditForm({ ...editForm, corporate_name: e.target.value })}
                     />
                   </div>
                   <div>
                       <label className="text-xs font-bold uppercase opacity-70">CNPJ</label>
                       <input
                         className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}
                         value={editForm.cnpj}
                         onChange={(e) => setEditForm({ ...editForm, cnpj: maskCnpjInput(e.target.value) })}
                       />
                     </div>
                   <div>
                     <label className="text-xs font-bold uppercase opacity-70">CRECI PJ</label>
                     <input
                       className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}
                       value={editForm.creci_pj}
                       onChange={(e) => setEditForm({ ...editForm, creci_pj: e.target.value })}
                     />
                   </div>
                   <div className="md:col-span-2">
                     <label className="text-xs font-bold uppercase opacity-70">Endereço</label>
                     <input
                       className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}
                       value={editForm.address}
                       onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                     />
                   </div>
                   <div>
                     <label className="text-xs font-bold uppercase opacity-70">Bairro</label>
                     <input
                       className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}
                       value={editForm.neighborhood}
                       onChange={(e) => setEditForm({ ...editForm, neighborhood: e.target.value })}
                     />
                   </div>
                   <div>
                       <label className="text-xs font-bold uppercase opacity-70">CEP</label>
                       <input
                         className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}
                         value={editForm.zip}
                         onChange={(e) => setEditForm({ ...editForm, zip: maskCepInput(e.target.value) })}
                       />
                     </div>
                   <div>
                     <label className="text-xs font-bold uppercase opacity-70">Email corporativo</label>
                     <input
                       className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}
                       value={editForm.email}
                       onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                     />
                   </div>
                   <div>
                     <label className="text-xs font-bold uppercase opacity-70">Website</label>
                     <input
                       className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}
                       value={editForm.website}
                       onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                     />
                   </div>
                 </div>

                 <div className={`border-t ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}></div>

                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div>
                     <label className="text-xs font-bold uppercase opacity-70">Sócio responsável</label>
                     <input
                       className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}
                       value={editForm.owner_name}
                       onChange={(e) => setEditForm({ ...editForm, owner_name: e.target.value })}
                     />
                   </div>
                   <div>
                       <label className="text-xs font-bold uppercase opacity-70">WhatsApp</label>
                       <input
                         className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}
                         value={editForm.owner_whatsapp}
                         onChange={(e) => setEditForm({ ...editForm, owner_whatsapp: maskWhatsappInput(e.target.value) })}
                       />
                     </div>
                   <div>
                     <label className="text-xs font-bold uppercase opacity-70">Email pessoal</label>
                     <input
                       className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}
                       value={editForm.owner_email}
                       onChange={(e) => setEditForm({ ...editForm, owner_email: e.target.value })}
                     />
                   </div>
                 </div>

                 <div className={`border-t ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}></div>

                 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                   <div>
                     <label className="text-xs font-bold uppercase opacity-70">Instagram</label>
                     <input
                       className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}
                       value={editForm.instagram}
                       onChange={(e) => setEditForm({ ...editForm, instagram: e.target.value })}
                     />
                   </div>
                   <div>
                     <label className="text-xs font-bold uppercase opacity-70">Facebook</label>
                     <input
                       className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}
                       value={editForm.facebook}
                       onChange={(e) => setEditForm({ ...editForm, facebook: e.target.value })}
                     />
                   </div>
                   <div>
                     <label className="text-xs font-bold uppercase opacity-70">LinkedIn</label>
                     <input
                       className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}
                       value={editForm.linkedin}
                       onChange={(e) => setEditForm({ ...editForm, linkedin: e.target.value })}
                     />
                   </div>
                 </div>
               </div>

               <div className={`px-6 py-4 border-t flex justify-end gap-3 ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
                 <button
                   onClick={closeEditModal}
                   className={`px-4 py-2 rounded-lg text-sm font-semibold ${darkMode ? 'bg-vision-surface text-gray-200' : 'bg-gray-100 text-gray-700'}`}
                 >
                   Cancelar
                 </button>
                 <button
                   onClick={handleSaveEdit}
                   className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#294b29] text-white hover:bg-[#1f371f] shadow"
                 >
                   Salvar
                 </button>
               </div>
             </div>
           </div>
         )}
         
         {/* Header Card */}
         <div className={`mb-6 p-6 rounded-2xl border flex flex-col gap-6 shadow-sm
             ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}
         `}>
             <div className="grid grid-cols-1 md:grid-cols-[auto,1fr,auto] items-center md:items-start gap-6 relative">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={selectedProfile.name}
                    className="w-20 h-20 rounded-2xl object-cover flex-shrink-0 mx-auto md:mx-0"
                    onError={() => setAvatarLoadFailed(true)}
                  />
                ) : (
                  <div className={`
                      w-20 h-20 rounded-2xl flex items-center justify-center text-3xl font-bold flex-shrink-0 mx-auto md:mx-0
                      ${darkMode ? 'bg-vision-surface text-gray-400' : 'bg-[#294b29] text-white'}
                  `}>
                  {selectedProfile.logo_initial}
                  </div>
                )}
                <div className="flex-1 text-center md:text-left">
                    <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedProfile.name}</h2>
                    <div className={`flex flex-wrap justify-center md:justify-start gap-4 mt-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {socialLinks.map(({ id, label, Icon, value }) =>
                          value ? (
                            <a
                              key={id}
                              href={getExternalUrl(value)}
                              target="_blank"
                              rel="noreferrer"
                              className="flex items-center gap-1 hover:underline"
                              title={label}
                            >
                              <Icon size={16} />
                            </a>
                          ) : (
                            <div key={id} className="flex items-center gap-1 opacity-50" title={label}>
                              <Icon size={16} />
                            </div>
                          )
                        )}
                        {/* CLICKABLE WEBSITE */}
                        {selectedProfile.registration.website && (
                           <button
                              onClick={() => openWebsite(selectedProfile.registration.website)}
                              className="flex items-center gap-1 hover:underline cursor-pointer"
                              title="Website"
                           >
                              <Globe size={16} />
                           </button>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mt-5">
                      {selectedProfile.products.length ? (
                        <>
                          {selectedProfile.products.slice(0, 6).map((product) => (
                            <span
                              key={product.id}
                              className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border ${
                                darkMode
                                  ? 'bg-vision-surface/60 text-gray-200 border-vision-border'
                                  : 'bg-gray-100 text-gray-700 border-gray-200'
                              }`}
                            >
                              {product.name}
                            </span>
                          ))}
                          {selectedProfile.products.length > 6 && (
                            <span
                              className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border ${
                                darkMode
                                  ? 'bg-vision-surface/60 text-gray-300 border-vision-border'
                                  : 'bg-gray-100 text-gray-600 border-gray-200'
                              }`}
                            >
                              +{selectedProfile.products.length - 6}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          Sem produtos vinculados
                        </span>
                      )}
                    </div>
                </div>
                
                {/* COMPACT STEPPER IN HEADER (Replaces Action Buttons) */}
                <div className="w-full md:w-auto mt-0">
                    <div className="flex flex-col items-center md:items-end gap-1">
                         <span className="text-[10px] uppercase font-bold opacity-50 mb-1 tracking-wider">Status do Credenciamento</span>
                         <div className="relative flex items-center justify-between w-full max-w-[200px] md:max-w-[220px] gap-2 mx-auto md:mx-0">
                            {/* Background Line */}
                            <div className="absolute left-2 right-2 top-[14px] h-0.5 bg-gray-200 dark:bg-gray-700 -z-0"></div>
                             {/* Progress Line */}
                            <div 
                                className="absolute left-2 top-[14px] h-0.5 bg-green-500 -z-0 transition-all duration-500"
                                style={{ width: `${Math.max(0, Math.min(1, currentStepIndex / (steps.length - 1))) * 100}%` }}
                            ></div>

                            {steps.map((step, index) => {
                                const Icon = step.icon;
                                const isActive = selectedProfile.status === step.id;
                                const isCompleted = currentStepIndex > index;

                                      return (
                                          <button 
                                              key={step.id}
                                              disabled={isBlocked}
                                              onClick={() => handleStepperStatusChange(step.id as AgencyStatus)}
                                              className="relative z-10 flex flex-col items-center gap-1 group disabled:opacity-50"
                                              title={step.label}
                                          >
                                        <div className={`
                                            w-7 h-7 rounded-full flex items-center justify-center border-2 transition-all duration-300
                                            ${isActive 
                                                ? 'bg-[#294b29] border-green-200 text-white shadow-md scale-110' 
                                                : isCompleted
                                                    ? 'bg-green-600 border-green-600 text-white'
                                                    : (darkMode ? 'bg-gray-800 border-gray-700 text-gray-500' : 'bg-white border-gray-300 text-gray-400')
                                            }
                                        `}>
                                            {isCompleted ? <Check size={14} /> : <Icon size={14} />}
                                        </div>
                                    </button>
                                );
                            })}
                         </div>
                         <div className="grid grid-cols-3 w-full max-w-[200px] md:max-w-[220px] text-[10px] font-medium mt-1 px-1 mx-auto md:mx-0">
              {steps.map((step) => (
                                 <span key={step.id} className={`text-center ${selectedProfile.status === step.id ? (darkMode ? 'text-white' : 'text-[#294b29]') : 'opacity-40'}`}>
                                     <span className="inline md:hidden">{step.shortLabel}</span>
                                     <span className="hidden md:inline">{step.label.split(' ')[0]}</span>
                                 </span>
                             ))}
                         </div>
                         <div className="flex items-center justify-center md:justify-end gap-2 mt-3 w-full flex-wrap">
                           {role === 'admin' && (
                             <button
                               onClick={handleSoftDelete}
                               className={`
                                 flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors
                                 ${darkMode
                                   ? 'bg-black/40 text-red-300 border-red-900 hover:bg-black/60'
                                   : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                                 }
                               `}
                             >
                               <Trash2 size={14} />
                               Excluir
                             </button>
                           )}
                           <button
                             onClick={handleToggleAccess}
                             className={`
                               flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors
                               ${isBlocked 
                                   ? (darkMode ? 'bg-green-900/30 text-green-400 border-green-800 hover:bg-green-900/50' : 'bg-green-100 text-green-700 border-green-200 hover:bg-green-200')
                                   : (darkMode ? 'bg-red-900/30 text-red-400 border-red-800 hover:bg-red-900/50' : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100')
                               }
                             `}
                           >
                             {isBlocked ? <CheckCircle2 size={14}/> : <Ban size={14}/>}
                             {isBlocked ? 'Reativar' : 'Suspender'}
                           </button>
                         </div>
                    </div>
                </div>
             </div>
         </div>

         {/* Tabs Navigation */}
         <div className={`flex overflow-x-auto border-b mb-6 scrollbar-hide gap-1
             ${darkMode ? 'border-vision-border' : 'border-gray-200'}
         `}>
            {[
            { id: 'dashboard', label: 'Visão Geral', icon: LayoutDashboard },
            { id: 'registration', label: 'Dados Cadastrais', icon: FileText },
              { id: 'team', label: 'Equipe', icon: Users },
              { id: 'tickets', label: 'Chamados', icon: MessageSquare },
              { id: 'settings', label: 'Configurações', icon: PenTool },
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id as RealEstateTab)}
                  className={`
                    flex items-center gap-2 px-6 py-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap
                    ${activeTab === tab.id 
                      ? (darkMode ? 'border-vision-activeRed text-white' : 'border-[#294b29] text-[#294b29]') 
                      : (darkMode ? 'border-transparent text-gray-500 hover:text-gray-300' : 'border-transparent text-gray-500 hover:text-gray-700')
                    }
                  `}
                >
                  <Icon size={18} /> {tab.label}
                </button>
              )
            })}
         </div>

         {/* TAB CONTENT: DASHBOARD */}
         {activeTab === 'dashboard' && (
           <div className="animate-in fade-in slide-in-from-bottom-2">

              <div className={`rounded-2xl border p-6 ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className={`p-6 rounded-xl border ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex justify-between items-start mb-4">
                          <div className={`p-2 rounded-lg ${darkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-700'}`}>
                            <DollarSign size={20} />
                          </div>
                      </div>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Vendas totais</p>
                      <p className="text-2xl font-bold mt-1">{selectedProfile.dashboard.total_sales}</p>
                    </div>

                    <div className={`p-6 rounded-xl border ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex justify-between items-start mb-4">
                          <div className={`p-2 rounded-lg ${darkMode ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                            <Users size={20} />
                          </div>
                      </div>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Equipe</p>
                      <p className="text-2xl font-bold mt-1">{selectedProfile.team.filter((member) => member.status === 'active').length}</p>
                    </div>

                    <div className={`p-6 rounded-xl border ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex justify-between items-start mb-4">
                          <div className={`p-2 rounded-lg ${darkMode ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-100 text-purple-700'}`}>
                            <MessageSquare size={20} />
                          </div>
                      </div>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Atendimentos</p>
                      <p className="text-2xl font-bold mt-1">{attendanceTotal ?? 0}</p>
                    </div>

                    <div className={`p-6 rounded-xl border ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex justify-between items-start mb-4">
                          <div className={`p-2 rounded-lg ${darkMode ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}`}>
                            <MapPin size={20} />
                          </div>
                      </div>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Visitas</p>
                      <p className="text-2xl font-bold mt-1">0</p>
                    </div>
                </div>
              </div>

           </div>
         )}
         {isProductsModalOpen && (
           <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
             <div className={`w-full max-w-2xl rounded-2xl shadow-2xl border ${darkMode ? 'bg-vision-darkGreen border-vision-border text-white' : 'bg-white border-gray-200 text-gray-800'}`}>
               <div className={`flex items-center justify-between px-6 py-4 border-b ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
                 <div>
                   <h3 className="text-lg font-bold">Vincular produtos</h3>
                   <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{selectedProfile?.name}</p>
                 </div>
                 <button onClick={closeProductsModal} className="text-sm font-semibold opacity-70 hover:opacity-100">Fechar</button>
               </div>

               <div className="max-h-[60vh] overflow-y-auto p-6 space-y-4">
                 {productsCatalogLoading && (
                   <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Carregando produtos...</p>
                 )}
                 {!productsCatalogLoading && productsCatalogError && (
                   <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-600'}`}>{productsCatalogError}</p>
                 )}
                 {!productsCatalogLoading && !productsCatalogError && productsCatalog.length === 0 && (
                   <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Nenhum produto disponível.</p>
                 )}
                 {!productsCatalogLoading && !productsCatalogError && productsCatalog.length > 0 && (
                   <div className="grid grid-cols-1 gap-3">
                     {productsCatalog.map((product) => {
                       const isSelected = productsSelection.includes(product.id);
                       return (
                         <button
                           key={product.id}
                           type="button"
                           onClick={() => toggleProductSelection(product.id)}
                           className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${
                             isSelected
                               ? (darkMode ? 'bg-vision-surface border-vision-border text-white' : 'bg-green-50 border-green-200 text-gray-900')
                               : (darkMode ? 'border-vision-border hover:bg-vision-surface/60' : 'border-gray-200 hover:bg-gray-50')
                           }`}
                         >
                           <span className="text-sm font-semibold">{product.name}</span>
                           <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${
                             isSelected
                               ? (darkMode ? 'border-green-500 text-green-300' : 'border-green-200 text-green-700')
                               : (darkMode ? 'border-vision-border text-gray-400' : 'border-gray-200 text-gray-500')
                           }`}>
                             {isSelected ? 'Vinculado' : 'Desvinculado'}
                           </span>
                         </button>
                       );
                     })}
                   </div>
                 )}
               </div>

               <div className={`px-6 py-4 border-t flex justify-end gap-3 ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
                 <button
                   onClick={closeProductsModal}
                   className={`px-4 py-2 rounded-lg text-sm font-semibold ${darkMode ? 'bg-vision-surface text-gray-200' : 'bg-gray-100 text-gray-700'}`}
                   disabled={productsSaving}
                 >
                   Cancelar
                 </button>
                 <button
                   onClick={handleSaveProducts}
                   className={`px-4 py-2 rounded-lg text-sm font-semibold text-white shadow ${
                     productsSaving || productsCatalogLoading || productsCatalogError
                       ? 'bg-gray-400 cursor-not-allowed'
                       : 'bg-[#294b29] hover:bg-[#1f371f]'
                   }`}
                   disabled={productsSaving || productsCatalogLoading || !!productsCatalogError}
                 >
                   {productsSaving ? 'Salvando...' : 'Salvar'}
                 </button>
               </div>
             </div>
           </div>
         )}

         {/* TAB CONTENT: REGISTRATION */}
         {activeTab === 'registration' && (
           <div className="animate-in fade-in slide-in-from-bottom-2">
             <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
               <div className={`p-6 rounded-xl border md:col-span-6 ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
                 <div className="flex items-start justify-between mb-6">
                   <div>
                     <h3 className="font-bold text-lg flex items-center gap-2">
                       <Building2 size={20} className={darkMode ? 'text-vision-activeRed' : 'text-[#294b29]'} />
                       Dados da Empresa
                     </h3>
                     <div className="flex items-center gap-2 mt-3">
                       <button
                         onClick={() => setRegistrationTab('empresa')}
                         className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                           registrationTab === 'empresa'
                             ? (darkMode ? 'bg-vision-surface text-white border-vision-border' : 'bg-[#294b29] text-white border-[#294b29]')
                             : (darkMode ? 'text-gray-400 border-vision-border hover:text-white' : 'text-gray-600 border-gray-200 hover:text-gray-900')
                         }`}
                       >
                         Empresa
                       </button>
                       <button
                         onClick={() => setRegistrationTab('socio')}
                         className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                           registrationTab === 'socio'
                             ? (darkMode ? 'bg-vision-surface text-white border-vision-border' : 'bg-[#294b29] text-white border-[#294b29]')
                             : (darkMode ? 'text-gray-400 border-vision-border hover:text-white' : 'text-gray-600 border-gray-200 hover:text-gray-900')
                         }`}
                       >
                         Sócio Representante
                       </button>
                     </div>
                   </div>
                   <button
                     onClick={openEditModal}
                     className={`flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-lg border transition-colors ${darkMode ? 'border-vision-border hover:bg-vision-surface' : 'border-gray-200 hover:bg-gray-100'}`}
                   >
                     <PenTool size={16} /> Editar
                   </button>
                 </div>
                 {registrationTab === 'empresa' && (
                   <>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                       <div>
                         <label className={`text-xs uppercase font-bold ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Razão Social</label>
                         <p className="text-sm font-medium mt-1">{selectedProfile.registration.corporate_name}</p>
                       </div>
                       <div>
                         <label className={`text-xs uppercase font-bold ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Nome Fantasia</label>
                         <p className="text-sm font-medium mt-1">{selectedProfile.registration.fantasy_name}</p>
                       </div>
                       <div>
                         <label className={`text-xs uppercase font-bold ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>CNPJ</label>
                         <p className="text-sm font-medium mt-1">{formatCnpj(selectedProfile.registration.cnpj)}</p>
                       </div>
                       <div>
                         <label className={`text-xs uppercase font-bold ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>CRECI PJ</label>
                         <p className="text-sm font-medium mt-1">{selectedProfile.registration.creci_pj}</p>
                       </div>
                       <div className="md:col-span-2">
                         <label className={`text-xs uppercase font-bold ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Endereço</label>
                         {/* CLICKABLE ADDRESS */}
                         <button 
                           onClick={() => openGoogleMaps(`${selectedProfile.registration.address}, ${selectedProfile.registration.neighborhood}, ${selectedProfile.registration.zip}`)}
                           className="text-sm font-medium mt-1 flex items-center gap-2 hover:underline cursor-pointer text-left"
                           title="Abrir no Google Maps"
                         >
                           {selectedProfile.registration.address}, {selectedProfile.registration.neighborhood} - {selectedProfile.registration.zip}
                           <ExternalLink size={12} className="opacity-50" />
                         </button>
                       </div>
                       <div className="md:col-span-2">
                         <label className={`text-xs uppercase font-bold ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Website</label>
                         {/* CLICKABLE WEBSITE IN TAB */}
                         {selectedProfile.registration.website ? (
                           <button 
                             onClick={() => openWebsite(selectedProfile.registration.website)}
                             className="text-sm font-medium mt-1 flex items-center gap-2 hover:underline cursor-pointer text-left"
                           >
                             {selectedProfile.registration.website}
                             <ExternalLink size={12} className="opacity-50" />
                           </button>
                         ) : (
                           <p className="text-sm font-medium mt-1">-</p>
                         )}
                       </div>
                       {role === 'admin' && (
                         <div className="md:col-span-2">
                           <button
                             onClick={openProductsModal}
                             className={`mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                               darkMode
                                 ? 'border-vision-border bg-vision-surface text-gray-200 hover:bg-vision-surface/80'
                                 : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                             }`}
                           >
                             Gerenciar produtos vinculados
                           </button>
                         </div>
                       )}
                     </div>

                   </>
                 )}
                 {registrationTab === 'socio' && (
                   <>
                     <h3 className="font-bold text-lg mb-6 flex items-center gap-2">
                       <Users size={20} className={darkMode ? 'text-vision-activeRed' : 'text-[#294b29]'} /> 
                       Sócio Representante
                     </h3>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                       <div>
                         <label className={`text-xs uppercase font-bold ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Nome Completo</label>
                         <p className="text-sm font-medium mt-1">{selectedProfile.registration.owner.name}</p>
                       </div>
                       <div>
                         <label className={`text-xs uppercase font-bold ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Contato (WhatsApp)</label>
                         <p className="text-sm font-medium mt-1">{selectedProfile.registration.owner.whatsapp}</p>
                       </div>
                       <div className="md:col-span-2">
                         <label className={`text-xs uppercase font-bold ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Email Pessoal</label>
                         <p className="text-sm font-medium mt-1">{selectedProfile.registration.owner.email}</p>
                       </div>
                     </div>
                   </>
                 )}
               </div>
               <div className={`p-6 rounded-xl border md:col-span-6 h-full max-h-[520px] flex flex-col ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
                   <div className="flex items-center justify-between mb-4">
                     <h3 className="font-bold text-lg flex items-center gap-2">
                       <FileText size={18} className={darkMode ? 'text-vision-activeRed' : 'text-[#294b29]'} />
                       Linha do tempo
                     </h3>
                     {auditLogsLoading && (
                       <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Carregando...</span>
                     )}
                   </div>

                   <div className="flex-1 min-h-0 overflow-y-auto pr-2" onScroll={handleAuditScroll}>
                     {auditLogsError && (
                       <div className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-600'}`}>{auditLogsError}</div>
                     )}

                     {!auditLogsLoading && !auditLogsError && auditLogs.length === 0 && (
                       <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Nenhum evento registrado.</div>
                     )}

                     {!auditLogsLoading && !auditLogsError && auditLogs.length > 0 && (
                       <div className="space-y-4">
                         {auditLogs.map((entry, index) => {
                           const { title, detail, detailLines, detailStrong } = describeAuditEvent(entry) as {
                             title: string;
                             detail: string;
                             detailLines?: string[];
                             detailStrong?: boolean;
                           };
                           return (
                             <div key={entry.id} className="flex gap-4">
                               <div className="flex flex-col items-center">
                                 <div className={`w-2.5 h-2.5 rounded-full ${darkMode ? 'bg-vision-activeRed' : 'bg-[#294b29]'}`} />
                                 {index !== auditLogs.length - 1 && (
                                   <div className={`w-px flex-1 ${darkMode ? 'bg-vision-border' : 'bg-gray-200'}`} />
                                 )}
                               </div>
                               <div className="flex-1">
                                 <div className="flex items-center justify-between gap-2">
                                   <p className="font-semibold text-sm">{title}</p>
                                   <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                     {formatTimelineDate(entry.createdAt)}
                                   </span>
                                 </div>
                                 {detailStrong ? (
                                   <p className={`text-xs mt-1 font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                     {detail}
                                   </p>
                                 ) : (
                                   <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{detail}</p>
                                 )}
                                 {detailLines && detailLines.length > 0 && (
                                   <div className="mt-2 space-y-1">
                                     {detailLines.map((line) => (
                                       <p key={line} className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                         {line}
                                       </p>
                                     ))}
                                   </div>
                                 )}
                               </div>
                             </div>
                           );
                         })}
                       </div>
                     )}
                     {auditLogsLoadingMore && (
                       <div className={`text-xs mt-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Carregando...</div>
                     )}
                   </div>
                 </div>
             </div>
           </div>
         )}
         {/* TAB CONTENT: TEAM */}
         {activeTab === 'team' && (
           <div className={`rounded-xl border overflow-hidden animate-in fade-in slide-in-from-bottom-2 ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
              
              {/* TEAM SEARCH INPUT */}
              <div className={`p-4 border-b ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="relative max-w-sm w-full">
                       <Search className={`absolute left-3 top-2.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} size={16} />
                       <input 
                          type="text" 
                          value={teamSearch}
                          onChange={(e) => setTeamSearch(e.target.value)}
                          placeholder="Buscar corretor por nome, email ou telefone..." 
                          className={`
                             w-full pl-9 pr-4 py-2 rounded-lg border outline-none text-sm transition-all
                             ${darkMode 
                                ? 'bg-vision-surface border-vision-border text-white focus:ring-1 focus:ring-gray-600' 
                                : 'bg-gray-50 border-gray-300 text-gray-800 focus:ring-1 focus:ring-[#294b29]'
                             }
                          `}
                       />
                    </div>
                    <button
                      type="button"
                      onClick={handleRefreshTeam}
                      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-all
                        ${darkMode
                          ? 'bg-vision-surface border-vision-border text-gray-200 hover:text-white hover:border-gray-500'
                          : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                        }
                      `}
                    >
                      <RefreshCw size={14} />
                      Atualizar
                    </button>
                  </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left">
                   <thead className={`${darkMode ? 'bg-vision-surface text-gray-400' : 'bg-gray-50 text-gray-600'} text-xs uppercase`}>
                      <tr>
                         <th className="px-6 py-4 font-semibold">Nome</th>
                         <th className="px-6 py-4 font-semibold">Função</th>
                         <th className="px-6 py-4 font-semibold">Contato</th>
                         <th className="px-6 py-4 font-semibold">Whatsapp</th>
                         <th className="px-6 py-4 font-semibold">Status</th>
                      </tr>
                   </thead>
                   <tbody className={`divide-y ${darkMode ? 'divide-vision-border' : 'divide-gray-100'}`}>
                      {filteredTeam.length > 0 ? filteredTeam.map(member => (
                         <tr key={member.id} className={darkMode ? 'hover:bg-vision-surface/50' : 'hover:bg-gray-50'}>
                            <td className="px-6 py-4">
                               <div className="flex items-center gap-3">
                                  {(() => {
                                    const isBroken = teamBrokenPhotoIds.has(member.id);
                                    if (member.photo && !isBroken) {
                                      return (
                                        <img
                                          src={member.photo}
                                          alt={member.name}
                                          className="w-8 h-8 rounded-full object-cover"
                                          onError={() => handleRefreshTeamPhoto(member)}
                                        />
                                      );
                                    }
                                    return (
                                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                                         {member.name.charAt(0)}
                                      </div>
                                    );
                                  })()}
                                  <a
                                    href={`/corretores/${member.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className={`font-medium hover:underline ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}
                                  >
                                    {member.name}
                                  </a>
                               </div>
                            </td>
                            <td className="px-6 py-4">
                               <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded text-xs font-medium 
                                  ${member.role === 'Gestor' 
                                    ? (darkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-800') 
                                    : (darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-800')}
                               `}>
                                  <Briefcase size={12} />
                                  {member.role}
                               </span>
                            </td>
                            <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                               <div className="flex flex-col gap-0.5">
                                  <span>{member.email}</span>
                                  <span className="text-xs opacity-70">{member.phone}</span>
                               </div>
                            </td>
                            <td className="px-6 py-4">
                               {(() => {
                                 const meta = getWhatsappStatusMeta(member.possuiWhatsapp ?? null);
                                 return (
                                   <span
                                     className={`inline-flex h-2.5 w-2.5 rounded-full ${meta.className}`}
                                     title={meta.label}
                                     aria-label={meta.label}
                                   />
                                 );
                               })()}
                            </td>
                            <td className="px-6 py-4">
                               <StatusBadge status={member.status} />
                            </td>
                         </tr>
                      )) : (
                         <tr>
                            <td colSpan={5} className="p-8 text-center text-gray-500">Nenhum membro encontrado.</td>
                         </tr>
                      )}
                   </tbody>
                </table>
              </div>
           </div>
         )}

         {/* TAB CONTENT: TICKETS */}
         {activeTab === 'tickets' && (
           <div className={`rounded-xl border overflow-hidden animate-in fade-in slide-in-from-bottom-2 ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
               <div className="overflow-x-auto">
                <table className="w-full text-left">
                   <thead className={`${darkMode ? 'bg-vision-surface text-gray-400' : 'bg-gray-50 text-gray-600'} text-xs uppercase`}>
                      <tr>
                         <th className="px-6 py-4 font-semibold">ID</th>
                         <th className="px-6 py-4 font-semibold">Assunto</th>
                         <th className="px-6 py-4 font-semibold">Solicitante</th>
                         <th className="px-6 py-4 font-semibold">Data</th>
                         <th className="px-6 py-4 font-semibold">Status</th>
                      </tr>
                   </thead>
                   <tbody className={`divide-y ${darkMode ? 'divide-vision-border' : 'divide-gray-100'}`}>
                      {ticketsLoading ? (
                         <tr>
                           <td colSpan={5} className="p-8 text-center text-gray-500">
                             <div className="flex items-center justify-center gap-2">
                               <Loader2 className="w-4 h-4 animate-spin" />
                               Carregando chamados...
                             </div>
                           </td>
                         </tr>
                      ) : ticketsError ? (
                         <tr>
                           <td colSpan={5} className="p-8 text-center text-red-500">{ticketsError}</td>
                         </tr>
                      ) : selectedProfile.tickets.length > 0 ? selectedProfile.tickets
                        .slice((ticketsPage - 1) * ticketsPageSize, ticketsPage * ticketsPageSize)
                        .map(ticket => (
                         <tr key={ticket.id} className={darkMode ? 'hover:bg-vision-surface/50' : 'hover:bg-gray-50'}>
                            <td className={`px-6 py-4 font-mono text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{ticket.id}</td>
                            <td className={`px-6 py-4 font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{ticket.subject}</td>
                            <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{ticket.requester}</td>
                            <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{ticket.date}</td>
                            <td className="px-6 py-4">
                               <StatusBadge status={ticket.status} />
                            </td>
                         </tr>
                      )) : (
                         <tr>
                            <td colSpan={5} className="p-8 text-center text-gray-500">
                               <div className="flex flex-col items-center gap-2">
                                  <AlertCircle size={32} className="opacity-20" />
                                  <p>Nenhum chamado registrado recentemente.</p>
                               </div>
                            </td>
                         </tr>
                      )}
                   </tbody>
                </table>
              </div>
              {!ticketsLoading && !ticketsError && selectedProfile.tickets.length > 0 && (
                <div className={`flex items-center justify-between px-6 py-4 border-t ${darkMode ? 'border-vision-border text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                  <span className="text-xs">
                    {((ticketsPage - 1) * ticketsPageSize) + 1}-
                    {Math.min(ticketsPage * ticketsPageSize, selectedProfile.tickets.length)} de {selectedProfile.tickets.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setTicketsPage(prev => Math.max(1, prev - 1))}
                      disabled={ticketsPage === 1}
                      className={`px-3 py-1.5 rounded text-xs font-semibold border ${
                        ticketsPage === 1
                          ? (darkMode ? 'border-vision-border text-gray-600' : 'border-gray-200 text-gray-300')
                          : (darkMode ? 'border-vision-border text-gray-200 hover:bg-vision-surface' : 'border-gray-200 text-gray-600 hover:bg-gray-100')
                      }`}
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => setTicketsPage(prev => Math.min(Math.ceil(selectedProfile.tickets.length / ticketsPageSize), prev + 1))}
                      disabled={ticketsPage >= Math.ceil(selectedProfile.tickets.length / ticketsPageSize)}
                      className={`px-3 py-1.5 rounded text-xs font-semibold border ${
                        ticketsPage >= Math.ceil(selectedProfile.tickets.length / ticketsPageSize)
                          ? (darkMode ? 'border-vision-border text-gray-600' : 'border-gray-200 text-gray-300')
                          : (darkMode ? 'border-vision-border text-gray-200 hover:bg-vision-surface' : 'border-gray-200 text-gray-600 hover:bg-gray-100')
                      }`}
                    >
                      Proxima
                    </button>
                  </div>
                </div>
              )}
           </div>
         )}

         {/* TAB CONTENT: SETTINGS */}
         {activeTab === 'settings' && (
           <div className="animate-in fade-in slide-in-from-bottom-2">
             <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
               <div className={`p-6 rounded-xl border lg:col-span-2 ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
                 <h3 className="font-bold text-lg flex items-center gap-2">
                   <PenTool size={18} className={darkMode ? 'text-vision-activeRed' : 'text-[#294b29]'} />
                   Configurações CVCRM
                 </h3>
                 <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                   Informe o ID da imobiliaria no CVCRM para habilitar a sincronização de corretores.
                 </p>
                 <div className="mt-5 grid grid-cols-1 md:grid-cols-[1fr,auto,auto] gap-3 items-end">
                   <div>
                     <label className={`text-xs uppercase font-bold ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>CV ID</label>
                     <input
                       type="text"
                       value={cvcrmIdInput}
                       onChange={(e) => setCvcrmIdInput(e.target.value)}
                       placeholder="Ex: 1234"
                       className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-vision-surface border-vision-border' : 'bg-gray-50 border-gray-200'}`}
                     />
                   </div>
                   <button
                     onClick={handleSaveCvcrmId}
                     disabled={!isCvcrmDirty || cvcrmSaving || cvcrmCapturing}
                     className={`px-4 py-2 rounded-lg text-sm font-semibold text-white shadow ${
                       !isCvcrmDirty || cvcrmSaving || cvcrmCapturing
                         ? 'bg-gray-400 cursor-not-allowed'
                         : 'bg-[#294b29] hover:bg-[#1f371f]'
                     }`}
                   >
                     {cvcrmSaving ? 'Salvando...' : 'Salvar CV ID'}
                   </button>
                   <button
                     onClick={handleCaptureCvcrmId}
                     disabled={cvcrmCapturing || cvcrmSaving}
                     className={`px-4 py-2 rounded-lg text-sm font-semibold text-white shadow ${
                       cvcrmCapturing || cvcrmSaving
                         ? 'bg-gray-400 cursor-not-allowed'
                         : 'bg-[#3b6b3b] hover:bg-[#2a4d2a]'
                     }`}
                   >
                     {cvcrmCapturing ? 'Capturando...' : 'Capturar CV ID'}
                   </button>
                 </div>
               </div>
               <div className={`p-6 rounded-xl border ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
                 <h3 className="font-bold text-lg">Sincronizar corretores</h3>
                 <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                   Importa todos os corretores cadastrados no CVCRM para esta imobiliaria.
                 </p>
                 <button
                   onClick={handleSyncCorretores}
                   disabled={!cvcrmIdInput.trim() || cvcrmSyncing}
                   className={`mt-4 w-full px-4 py-2 rounded-lg text-sm font-semibold text-white shadow ${
                     !cvcrmIdInput.trim() || cvcrmSyncing
                       ? 'bg-gray-400 cursor-not-allowed'
                       : 'bg-[#294b29] hover:bg-[#1f371f]'
                   }`}
                 >
                   {cvcrmSyncing ? 'Sincronizando...' : 'Sincronizar corretores'}
                 </button>
               </div>
               <div className={`p-6 rounded-xl border lg:col-span-3 ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
                 <div className="flex items-start justify-between gap-4 flex-wrap">
                   <div>
                     <h3 className="font-bold text-lg flex items-center gap-2">
                       <Tag size={18} className={darkMode ? 'text-vision-activeRed' : 'text-[#294b29]'} />
                       Tags da imobiliaria
                     </h3>
                     <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                       Use tags para segmentar imobiliarias na coordenação e no funil de parceria.
                     </p>
                   </div>
                   <div className="flex items-center gap-2">
                     <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                       {agencyTags.length} tag(s)
                     </span>
                     <button
                       type="button"
                       onClick={() => {
                         setAgencyTags([]);
                         setTagSuccess(null);
                       }}
                       className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                         darkMode ? 'border-vision-border text-gray-300 hover:bg-vision-surface' : 'border-gray-200 text-gray-600 hover:bg-gray-100'
                       }`}
                     >
                       Limpar
                     </button>
                   </div>
                 </div>

                 <div className="mt-4">
                   <label className={`text-xs uppercase font-bold ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Adicionar tag</label>
                   <div className="mt-2 flex flex-col gap-2">
                     <input
                       type="text"
                       value={tagInput}
                       onChange={(e) => setTagInput(e.target.value)}
                       onKeyDown={(e) => {
                         if (e.key === 'Enter') {
                           e.preventDefault();
                           handleAddTag(tagInput);
                         }
                       }}
                       placeholder="Ex: premium, pendente-doc, lote-1"
                       className={`w-full px-3 py-2 rounded-lg border text-sm ${
                         darkMode ? 'bg-vision-surface border-vision-border text-gray-100' : 'bg-gray-50 border-gray-200'
                       }`}
                     />
                     {tagSuggestions.length > 0 && (
                       <div className="flex flex-wrap gap-2">
                         {tagSuggestions.map((tag) => (
                           <button
                             key={tag.id}
                             type="button"
                             onClick={() => handleAddTag(tag.name)}
                             className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                               darkMode ? 'border-vision-border text-gray-300 hover:bg-vision-surface' : 'border-gray-200 text-gray-600 hover:bg-gray-100'
                             }`}
                           >
                             + {tag.name}
                           </button>
                         ))}
                       </div>
                     )}
                   </div>
                 </div>

                 <div className="mt-4 flex flex-wrap gap-2">
                   {agencyTags.length > 0 ? (
                     agencyTags.map((tag) => (
                       <span
                         key={`${tag.id}-${tag.name}`}
                         className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border ${
                           darkMode ? 'border-vision-border text-gray-200' : 'border-gray-200 text-gray-700'
                         }`}
                       >
                         {tag.name}
                         <button
                           type="button"
                           onClick={() => handleRemoveTag(tag.name)}
                           className={`p-0.5 rounded-full ${darkMode ? 'hover:bg-vision-surface' : 'hover:bg-gray-100'}`}
                           aria-label={`Remover tag ${tag.name}`}
                         >
                           <X size={12} />
                         </button>
                       </span>
                     ))
                   ) : (
                     <span className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Nenhuma tag aplicada.</span>
                   )}
                 </div>

                 {tagError && <p className="text-sm text-red-500 mt-3">{tagError}</p>}
                 {tagSuccess && <p className="text-sm text-green-600 mt-3">{tagSuccess}</p>}

                 <div className="mt-4 flex items-center gap-3">
                   <button
                     type="button"
                     onClick={handleSaveTags}
                     disabled={!isTagsDirty || tagSaving}
                     className={`px-4 py-2 rounded-lg text-sm font-semibold text-white shadow ${
                       !isTagsDirty || tagSaving
                         ? 'bg-gray-400 cursor-not-allowed'
                         : 'bg-[#294b29] hover:bg-[#1f371f]'
                     }`}
                   >
                     {tagSaving ? 'Salvando...' : 'Salvar tags'}
                   </button>
                   {!isTagsDirty && (
                     <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Tudo salvo.</span>
                   )}
                 </div>
               </div>
             </div>
           </div>
         )}

      </div>
    );
  }

  if (viewMode === 'detail') {
    if (profileError) {
      return (
        <div className={`p-6 md:p-8 min-h-full ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
          <div className={`rounded-xl border p-6 ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
            <div className="flex items-start gap-3">
              <AlertCircle className={`${darkMode ? 'text-red-300' : 'text-red-600'}`} size={20} />
              <div>
                <p className="font-semibold">{profileError}</p>
                <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Verifique sua sessao ou tente novamente mais tarde.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    onClick={handleBackToList}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold ${darkMode ? 'bg-vision-surface text-gray-100' : 'bg-gray-100 text-gray-700'}`}
                  >
                    Voltar para lista
                  </button>
                  {initialAgencyId ? (
                    <button
                      onClick={() => handleSelectAgency(initialAgencyId)}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold ${darkMode ? 'bg-vision-activeRed text-white' : 'bg-[#294b29] text-white'}`}
                    >
                      Tentar novamente
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className={`p-6 md:p-8 min-h-full ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
        <div className="flex items-center gap-3">
          <Loader2 className={`w-6 h-6 animate-spin ${darkMode ? 'text-white' : 'text-[#294b29]'}`} />
          <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Carregando perfil...</span>
        </div>
      </div>
    );
  }

  return null;
};

export default RealEstatePage;





































